import React, { useState, useRef, useEffect, useCallback } from "react";
import { saveUserData, loadUserData } from "./firebase";
import { SAMPLE_NEWS_IMAGE } from "./sampleImage";
import { translateText } from "./bbn-text-utils";

const MONTHS=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const SAMPLE_CAPTION = "दिल्ली-NCR में बारिश का कहर, मौसम विभाग ने दी चेतावनी!";

// ══════════════════════════════════════════════════════════════
//  CLOUDINARY CONFIG — change only these two values if needed
// ══════════════════════════════════════════════════════════════
const CLD_CLOUD  = "dlzowheen";
const CLD_PRESET = "BBN Multiplex";
// eslint-disable-next-line no-unused-vars
const CLD_BASE   = `https://res.cloudinary.com/${CLD_CLOUD}/image/upload`;

// Upload file to Cloudinary, returns secure_url (original quality)
async function uploadToCloudinary(file, onProgress) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("upload_preset", CLD_PRESET);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `https://api.cloudinary.com/v1_1/${CLD_CLOUD}/image/upload`);
    xhr.upload.onprogress = e => { if(e.lengthComputable && onProgress) onProgress(Math.round(e.loaded/e.total*100)); };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if(data.secure_url) resolve(data.secure_url);
        else reject(new Error(data.error?.message || "Upload failed"));
      } catch { reject(new Error("Parse error")); }
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(fd);
  });
}

// localStorage helpers — persist all settings across refresh
const LS_KEY = "bbn_pnm_v1";
function lsSave(obj) { try { localStorage.setItem(LS_KEY, JSON.stringify(obj)); } catch {} }
function lsLoad() { try { const d = localStorage.getItem(LS_KEY); return d ? JSON.parse(d) : null; } catch { return null; } }
const TEMPLATES=[
  {id:"classic",   label:"Classic",    emoji:"📰", desc:"Clean photo + news bar"},
  {id:"splitdark", label:"Split Dark", emoji:"🌓", desc:"Dark cinematic theme"},
  {id:"fullbleed", label:"Full Bleed", emoji:"🖼️", desc:"Full photo with overlay"},
  {id:"gold",      label:"Gold Elite", emoji:"✨", desc:"Premium gold accent"},
  {id:"ticker",    label:"Ticker",     emoji:"📡", desc:"Live ticker style"},
  {id:"redbreak",  label:"Red Break",  emoji:"🔴", desc:"Bold breaking news"},
  {id:"darkdrama", label:"Dark Drama", emoji:"🌙", desc:"Dramatic dark look"},
  {id:"newspaper", label:"Newspaper",  emoji:"🗞️", desc:"Classic print style"},
  {id:"cinematic", label:"Cinematic",  emoji:"🎬", desc:"Widescreen film look"},
  {id:"neonpulse", label:"Neon Pulse", emoji:"💜", desc:"Vibrant neon glow"},
  {id:"magazine",  label:"Magazine",   emoji:"📖", desc:"Editorial magazine"},
  {id:"election",  label:"Election",   emoji:"🗳️", desc:"Results board style"},
];
const STEPS=[
  {id:"template", icon:"🎨", label:"Template"},
  {id:"photo",    icon:"📸", label:"Photo"},
  {id:"text",     icon:"✍",  label:"Text"},
  {id:"logo",     icon:"📰", label:"Logo"},
];
const LANG_OPTIONS=[
  {code:"hi",label:"Hindi 🇮🇳"},
  {code:"en",label:"English 🇬🇧"},
  {code:"ur",label:"Urdu"},
  {code:"bn",label:"Bengali"},
  {code:"mr",label:"Marathi"},
  {code:"ta",label:"Tamil"},
  {code:"te",label:"Telugu"},
];
const PHOTO_FILTERS=[
  {id:"none",label:"Normal"},{id:"grayscale(100%)",label:"B&W"},
  {id:"sepia(80%)",label:"Sepia"},{id:"contrast(1.4) saturate(1.6)",label:"Vivid"},
  {id:"contrast(1.5) brightness(0.85)",label:"Deep"},
  {id:"saturate(0) contrast(1.3) brightness(1.1)",label:"Matte"},
  {id:"hue-rotate(180deg)",label:"Neon"},
  {id:"invert(10%) sepia(20%) saturate(1.4)",label:"Cinematic"},
];
// eslint-disable-next-line no-unused-vars
const WORD_COLORS=[
  {c:"#CC0000",label:"Red",  fg:"#fff"},{c:"#D4A520",label:"Gold", fg:"#000"},
  {c:"#ffffff",label:"White",fg:"#111"},{c:"#4CAF50",label:"Green",fg:"#fff"},
  {c:"#FF6D00",label:"Orange",fg:"#fff"},{c:"#2196F3",label:"Blue", fg:"#fff"},
  {c:"#E91E63",label:"Pink", fg:"#fff"},{c:"#FFFF00",label:"Yellow",fg:"#111"},
];
const SWATCHES_BADGE =["#CC0000","#D4A520","#0d47a1","#1b5e20","#111","#7b1fa2","#e65100","#006064"];
const SWATCHES_STRIP =["#CC0000","#D4A520","#0d47a1","#fff","#111","#1b5e20"];
const SWATCHES_TICKER=["#CC0000","#D4A520","#0d47a1","#1b5e20","#111"];
const SWATCHES_LOGO_BORDER=["#D4A520","#CC0000","#fff","#4CAF50","#2196F3","#ff6d00","#7b1fa2","#000"];
const BOXBG=[["#ffffff","#111","White"],["#0a0a0a","#fff","Black"],["#1a0000","#fff","D.Red"],["#0d0d1a","#fff","D.Blue"],["#0d1a0d","#fff","D.Grn"],["#1a1500","#fff","D.Gold"]];
const HLCOL=[["#111","#fff","Black"],["#fff","#111","White"],["#CC0000","#fff","Red"],["#D4A520","#000","Gold"],["#4CAF50","#fff","Green"]];
const LOGO_POS=[["tl","↖"],["tm","↑"],["tr","↗"],["ml","◀"],["mr","▶"],["bl","↙"],["bm","↓"],["br","↘"]];

// ── HTML → segments with color, bold, italic, font, size ──────────────────────
function parseSegs(html){
  if(!html) return [{t:"Write your headline here",c:null,b:false,i:false,f:null,sz:null}];
  if(typeof document !== "undefined"){
    const div = document.createElement("div");
    div.innerHTML = html;
    const segs = [];
    function walk(node, ctx){
      if(node.nodeType === 3){
        const txt = node.textContent;
        if(txt) segs.push({t:txt, c:ctx.c, b:ctx.b, i:ctx.i, f:ctx.f, sz:ctx.sz});
        return;
      }
      if(node.nodeType !== 1) return;
      const tag = node.tagName.toLowerCase();
      const style = node.getAttribute("style")||"";
      const next = {...ctx};
      if(tag==="b"||tag==="strong") next.b=true;
      if(tag==="i"||tag==="em")     next.i=true;
      if(tag==="u")                  next.u=true;
      const cm = style.match(/color\s*:\s*([^;]+)/i);
      if(cm) next.c = cm[1].trim();
      const fm = style.match(/font-family\s*:\s*([^;]+)/i);
      if(fm) next.f = fm[1].trim().replace(/['"]/g,"");
      const fsm = node.getAttribute("size");
      if(fsm) next.sz = parseInt(fsm);
      for(const child of node.childNodes) walk(child, next);
    }
    walk(div, {c:null,b:false,i:false,u:false,f:null,sz:null});
    const merged=[];
    for(const s of segs){
      const last=merged[merged.length-1];
      if(last&&last.c===s.c&&last.b===s.b&&last.i===s.i&&last.f===s.f&&last.sz===s.sz)
        last.t+=s.t;
      else merged.push({...s});
    }
    const result=merged.filter(s=>s.t);
    return result.length ? result : [{t:div.textContent||"Write your headline here",c:null,b:false,i:false,f:null,sz:null}];
  }
  const plain=html.replace(/<[^>]+>/g,"").replace(/&nbsp;/g," ").replace(/&lt;/g,"<").replace(/&gt;/g,">");
  return [{t:plain||"Write your headline here",c:null,b:false,i:false,f:null,sz:null}];
}

// eslint-disable-next-line no-unused-vars
const FONT_SIZE_MAP={1:10,2:13,3:16,4:18,5:24,6:32,7:48};

// ══════════════════════════════════════════════════════════════
// TEXT OVERLAP FIX — wrapSegs + drawColorHL completely rewritten
// Key changes:
//   1. textBaseline = "top" (was "middle") → reliable multi-line Y
//   2. measureTextH() → pre-calculate height BEFORE drawing
//   3. All templates now call measureTextH() to get real height
//      and position headline dynamically (no more hardcoded offsets)
// ══════════════════════════════════════════════════════════════

// ── COMPLETELY REWRITTEN WRAP + DRAW (no token splitting, no space tokens) ──
// Approach: wrap words into string lines, draw each line centered with fillText
// This avoids all token-width accumulation errors that cause overlap/cutoff

function buildLines(ctx, text, fontStr, maxW) {
  ctx.font = fontStr;
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (const word of words) {
    const test = cur ? cur + " " + word : word;
    if (ctx.measureText(test).width > maxW && cur) {
      lines.push(cur);
      cur = word;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [""];
}

// Returns total pixel height
function measureTextH(ctx, segs, maxW, fsPx) {
  const text = segs.map(s => s.t).join("") || "Text";
  const style  = segs[0]?.i ? "italic" : "normal";
  const weight = "900";
  const family = segs[0]?.f ? `'${segs[0].f}',Arial,sans-serif` : "Arial,sans-serif";
  const fontStr = `${style} ${weight} ${fsPx}px ${family}`;
  const lines = buildLines(ctx, text, fontStr, maxW);
  return lines.length * fsPx * 1.45;
}

// Draw headline — cx=center X, topY=top Y, maxW=max line width
function drawColorHL(ctx, segs, cx, topY, maxW, fsPx, defColor) {
  if (!segs || !segs.length) return 0;
  const text = segs.map(s => s.t).join("") || "Text";
  const style  = segs[0]?.i ? "italic" : "normal";
  const weight = "900";
  const family = segs[0]?.f ? `'${segs[0].f}',Arial,sans-serif` : "Arial,sans-serif";
  const color  = segs[0]?.c || defColor || "#111";
  const fontStr = `${style} ${weight} ${fsPx}px ${family}`;

  ctx.save();
  ctx.font = fontStr;
  ctx.fillStyle = color;
  ctx.textAlign = "center";   // center align — no manual x calculation
  ctx.textBaseline = "top";

  const lines = buildLines(ctx, text, fontStr, maxW);
  const lh = fsPx * 1.45;

  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], cx, topY + i * lh);
  }

  ctx.restore();
  return lines.length * lh;
}

// ── CANVAS ENGINE ─────────────────────────────────────────────
function drawCard(canvas,photoImg,logoImg,cfg){
  const ctx=canvas.getContext("2d");
  const W=canvas.width,H=canvas.height;
  ctx.clearRect(0,0,W,H);
  const {template,headSegs,source,badgeText,liveLabel,badgeColor,stripColor,boxBg,hlColor,tickerColor,
    ovLogo,ovLive,ovBadge,ovStrip,ovDivider,ovSource,fontSize,logoSize,logoPosKey,
    logoBorderColor,logoBorderWidth,logoShape,photoPos,photoH,brightness,contrast,
    saturation,blur,vignette,warmth,photoFilter}=cfg;
  const sc=W/400;
  const fsPx=Math.max(10,(fontSize||5.5)*sc*5.2);
  const pH_r=(photoH||50)/100;
  const PAD=14*sc;
  const FOOTER=24*sc;

  function CR(x,y,w,h,fn){ctx.save();ctx.beginPath();ctx.rect(x,y,w,h);ctx.clip();fn();ctx.restore();}
  // eslint-disable-next-line no-unused-vars
  function CRR(x,y,w,h,r,fn){ctx.save();ctx.beginPath();if(ctx.roundRect)ctx.roundRect(x,y,w,h,r);else ctx.rect(x,y,w,h);ctx.clip();fn();ctx.restore();}

  function dPhoto(x,y,w,h){
    if(!photoImg){
      // Premium placeholder
      const pg=ctx.createLinearGradient(x,y,x+w,y+h);
      pg.addColorStop(0,"#1a1a2e");pg.addColorStop(1,"#0d0d1a");
      ctx.fillStyle=pg;ctx.fillRect(x,y,w,h);
      ctx.save();ctx.font=`300 ${11*sc}px Arial`;ctx.fillStyle="rgba(255,255,255,0.2)";
      ctx.textAlign="center";ctx.textBaseline="middle";ctx.fillText("📸 Upload Photo",x+w/2,y+h/2);ctx.restore();
      return;
    }
    CR(x,y,w,h,()=>{
      const iW=photoImg.naturalWidth||1,iH=photoImg.naturalHeight||1;
      const scale=Math.max(w/iW,h/iH);
      const dW=iW*scale,dH=iH*scale;
      const dx=x+(w-dW)/2,dy=y+(h-dH)*(photoPos||0.2);
      let f=`brightness(${(brightness||100)/100}) contrast(${(contrast||100)/100}) saturate(${(saturation||100)/100})`;
      if(blur>0)f+=` blur(${blur}px)`;
      if(photoFilter&&photoFilter!=="none")f+=` ${photoFilter}`;
      ctx.filter=f;ctx.drawImage(photoImg,dx,dy,dW,dH);ctx.filter="none";
      if(warmth>0){ctx.save();ctx.globalAlpha=warmth/400;ctx.fillStyle="#ff8c00";ctx.fillRect(x,y,w,h);ctx.restore();}
      else if(warmth<0){ctx.save();ctx.globalAlpha=Math.abs(warmth)/400;ctx.fillStyle="#0060ff";ctx.fillRect(x,y,w,h);ctx.restore();}
      if(vignette>0){const vg=ctx.createRadialGradient(x+w/2,y+h/2,h*0.2,x+w/2,y+h/2,h*0.85);vg.addColorStop(0,"rgba(0,0,0,0)");vg.addColorStop(1,`rgba(0,0,0,${vignette/100})`);ctx.fillStyle=vg;ctx.fillRect(x,y,w,h);}
    });
  }

  function dLogo(){
    if(!ovLogo)return;
    const sz=(logoSize||10)*sc*5;
    let lx=W-sz-10*sc,ly=8*sc;
    if(logoPosKey==="tl"){lx=10*sc;ly=8*sc;}else if(logoPosKey==="tm"){lx=(W-sz)/2;ly=8*sc;}
    else if(logoPosKey==="tr"){lx=W-sz-10*sc;ly=8*sc;}else if(logoPosKey==="ml"){lx=10*sc;ly=(H-sz)/2;}
    else if(logoPosKey==="mr"){lx=W-sz-10*sc;ly=(H-sz)/2;}else if(logoPosKey==="bl"){lx=10*sc;ly=H-sz-70*sc;}
    else if(logoPosKey==="bm"){lx=(W-sz)/2;ly=H-sz-70*sc;}else if(logoPosKey==="br"){lx=W-sz-10*sc;ly=H-sz-70*sc;}
    const cx2=lx+sz/2,cy2=ly+sz/2;
    const isCircle=logoShape==="circle",isSquare=logoShape==="square";
    // Shadow
    ctx.save();ctx.shadowColor="rgba(0,0,0,0.7)";ctx.shadowBlur=14*sc;
    ctx.globalAlpha=0.93;ctx.fillStyle="#0a0a0a";ctx.beginPath();
    if(isSquare){if(ctx.roundRect)ctx.roundRect(lx,ly,sz,sz,8*sc);else ctx.rect(lx,ly,sz,sz);}
    else if(!isCircle){ctx.save();ctx.translate(cx2,cy2);ctx.rotate(Math.PI/4);ctx.rect(-sz*0.4,-sz*0.4,sz*0.8,sz*0.8);ctx.restore();}
    else{ctx.arc(cx2,cy2,sz/2,0,Math.PI*2);}
    ctx.fill();ctx.restore();
    // Border
    ctx.save();ctx.strokeStyle=logoBorderColor||"#D4A520";ctx.lineWidth=(logoBorderWidth||1.5)*sc;ctx.beginPath();
    if(isSquare){if(ctx.roundRect)ctx.roundRect(lx,ly,sz,sz,8*sc);else ctx.rect(lx,ly,sz,sz);}
    else if(!isCircle){ctx.save();ctx.translate(cx2,cy2);ctx.rotate(Math.PI/4);ctx.rect(-sz*0.4,-sz*0.4,sz*0.8,sz*0.8);ctx.restore();}
    else{ctx.arc(cx2,cy2,sz/2,0,Math.PI*2);}
    ctx.stroke();ctx.restore();
    // Clip + draw
    ctx.save();ctx.beginPath();
    if(isSquare){if(ctx.roundRect)ctx.roundRect(lx,ly,sz,sz,8*sc);else ctx.rect(lx,ly,sz,sz);}
    else if(!isCircle){ctx.save();ctx.translate(cx2,cy2);ctx.rotate(Math.PI/4);ctx.rect(-sz*0.4,-sz*0.4,sz*0.8,sz*0.8);ctx.restore();}
    else{ctx.arc(cx2,cy2,sz/2-1*sc,0,Math.PI*2);}
    ctx.clip();
    if(logoImg){ctx.drawImage(logoImg,lx,ly,sz,sz);}
    else{
      const g=ctx.createLinearGradient(lx,ly,lx,ly+sz);
      g.addColorStop(0,"#F5D77A");g.addColorStop(0.55,"#D4A520");g.addColorStop(1,"#8B6508");
      ctx.font=`900 ${sz*0.36}px Georgia,serif`;ctx.textAlign="center";ctx.textBaseline="middle";
      ctx.fillStyle=g;ctx.fillText("BBN",cx2,cy2+0.5);
    }
    ctx.restore();
  }

  function dStrip(h=4,color){
    if(!ovStrip)return;
    // Premium multi-layer strip
    const c=color||stripColor||"#CC0000";
    ctx.fillStyle=c;ctx.fillRect(0,0,W,h*sc);
    ctx.save();ctx.globalAlpha=0.4;ctx.fillStyle="#fff";ctx.fillRect(0,0,W,1*sc);ctx.restore();
  }

  function dBadge(cx,by,whiteBg=false){
    if(!ovBadge)return 0;
    const txt=badgeText||"⚡ Breaking News";
    ctx.save();ctx.font=`700 ${9*sc}px Arial,sans-serif`;
    const bW=ctx.measureText(txt).width+24*sc,bH=19*sc,bx=cx-bW/2;
    if(whiteBg){
      ctx.fillStyle="#fff";ctx.beginPath();if(ctx.roundRect)ctx.roundRect(bx,by,bW,bH,4*sc);else ctx.rect(bx,by,bW,bH);ctx.fill();
      ctx.fillStyle=badgeColor||"#CC0000";
    } else {
      ctx.fillStyle=badgeColor||"#CC0000";ctx.beginPath();if(ctx.roundRect)ctx.roundRect(bx,by,bW,bH,4*sc);else ctx.rect(bx,by,bW,bH);ctx.fill();
      // Subtle shine
      ctx.save();ctx.globalAlpha=0.18;ctx.fillStyle="#fff";
      ctx.beginPath();if(ctx.roundRect)ctx.roundRect(bx,by,bW,bH/2,4*sc);else ctx.rect(bx,by,bW,bH/2);ctx.fill();ctx.restore();
      ctx.fillStyle="#fff";
    }
    ctx.textAlign="center";ctx.textBaseline="middle";ctx.fillText(txt,cx,by+bH/2);ctx.restore();
    return bH;
  }

  function dOutlineBadge(cx,by,color="#D4A520"){
    if(!ovBadge)return 0;
    const txt=badgeText||"⚡ Breaking News";
    ctx.save();ctx.font=`700 ${9*sc}px Arial,sans-serif`;
    const bW=ctx.measureText(txt).width+24*sc,bH=19*sc,bx=cx-bW/2;
    ctx.strokeStyle=color;ctx.lineWidth=1.5*sc;ctx.beginPath();
    if(ctx.roundRect)ctx.roundRect(bx,by,bW,bH,4*sc);else ctx.rect(bx,by,bW,bH);ctx.stroke();
    ctx.fillStyle=color;ctx.textAlign="center";ctx.textBaseline="middle";ctx.fillText(txt,cx,by+bH/2);
    ctx.restore();return bH;
  }

  const segs = headSegs || [{t:"News headline here",c:null,b:true,i:false,f:null,sz:null}];
  const maxW = W - 32*sc;
  function getHL_H(){return measureTextH(ctx,segs,maxW,fsPx);}
  function dHL(cx,zoneTop,zoneH,defColor){
    const hlH=getHL_H();
    const topY=zoneTop+Math.max(PAD,(zoneH-FOOTER-hlH)/2);
    drawColorHL(ctx,segs,cx,topY,maxW,fsPx,defColor||hlColor||"#111");
  }
  function dSrc(cx,y,color){
    if(!ovSource)return;
    ctx.save();ctx.font=`600 ${7*sc}px Trebuchet MS,sans-serif`;
    ctx.fillStyle=color||"#999";ctx.textAlign="center";ctx.textBaseline="middle";
    ctx.fillText((source||"BBN NEWS").toUpperCase(),cx,y);ctx.restore();
  }
  function dDiv(cx,y,color){
    if(!ovDivider)return;
    ctx.save();ctx.globalAlpha=0.35;ctx.fillStyle=color||"#333";
    const dW=100*sc,dc=6*sc;
    ctx.fillRect(cx-dW/2,y,dW/2-dc,1.5*sc);ctx.fillRect(cx+dc,y,dW/2-dc,1.5*sc);
    ctx.save();ctx.translate(cx,y);ctx.rotate(Math.PI/4);ctx.fillRect(-3*sc,-3*sc,6*sc,6*sc);ctx.restore();
    ctx.restore();
  }
  function dGoldDiv(cx,y){
    if(!ovDivider)return;
    const dg=ctx.createLinearGradient(cx-90*sc,0,cx+90*sc,0);
    dg.addColorStop(0,"rgba(212,165,32,0)");dg.addColorStop(0.5,"#D4A520");dg.addColorStop(1,"rgba(212,165,32,0)");
    ctx.fillStyle=dg;ctx.fillRect(cx-90*sc,y,180*sc,1.5*sc);
  }
  function dTicker(bY,bH){
    // Premium ticker with gradient
    const tg=ctx.createLinearGradient(0,bY,0,bY+bH);
    tg.addColorStop(0,tickerColor||"#CC0000");tg.addColorStop(1,"rgba(0,0,0,0.3)");
    ctx.fillStyle=tg;ctx.fillRect(0,bY,W,bH);
    let pW=0;
    if(ovLive){
      pW=54*sc;
      const lg=ctx.createLinearGradient(0,bY,pW,bY);
      lg.addColorStop(0,"#6B0000");lg.addColorStop(1,"#8B0000");
      ctx.fillStyle=lg;ctx.fillRect(0,bY,pW,bH);
      // LIVE dot
      ctx.save();ctx.fillStyle="#ff4444";ctx.beginPath();ctx.arc(10*sc,bY+bH/2,3*sc,0,Math.PI*2);ctx.fill();ctx.restore();
      ctx.save();ctx.font=`800 ${8*sc}px Trebuchet MS,sans-serif`;ctx.fillStyle="#fff";
      ctx.textAlign="center";ctx.textBaseline="middle";ctx.fillText(liveLabel||"LIVE",pW/2+5*sc,bY+bH/2);ctx.restore();
      // Separator
      ctx.save();ctx.globalAlpha=0.3;ctx.fillStyle="#fff";ctx.fillRect(pW,bY+4*sc,1*sc,bH-8*sc);ctx.restore();
    }
    CR(pW+6*sc,bY+2*sc,W-pW-12*sc,bH-4*sc,()=>{
      const plain=(headSegs||[]).map(s=>s.t).join("");
      ctx.font=`700 ${9*sc}px Arial,sans-serif`;ctx.fillStyle="#fff";ctx.textAlign="left";ctx.textBaseline="middle";
      ctx.fillText(plain,pW+12*sc,bY+bH/2);
    });
  }

  // ═══════════════════════════════════════════════════════════
  // PREMIUM TEMPLATES
  // ═══════════════════════════════════════════════════════════

  if(template==="classic"){
    // Premium Classic — clean card with subtle shadow line
    const pH=Math.floor(H*pH_r),hH=H-pH;
    dPhoto(0,0,W,pH);
    dStrip(5);dLogo();
    // White box with subtle top border
    ctx.fillStyle=boxBg||"#fff";ctx.fillRect(0,pH,W,hH);
    // Color accent bar
    const ab=ctx.createLinearGradient(0,0,W,0);
    ab.addColorStop(0,badgeColor||"#CC0000");ab.addColorStop(1,"rgba(0,0,0,0.8)");
    ctx.fillStyle=ab;ctx.fillRect(0,pH,W,4*sc);
    // Subtle side accent
    ctx.fillStyle=badgeColor||"#CC0000";ctx.fillRect(0,pH+4*sc,3*sc,hH-4*sc);
    const bH=dBadge(W/2,pH+PAD);
    dHL(W/2,pH+PAD+bH+6*sc,hH-PAD-bH-6*sc-FOOTER,hlColor||"#111");
    dDiv(W/2,pH+hH-FOOTER,"#bbb");dSrc(W/2,pH+hH-FOOTER/2,"#888");

  }else if(template==="splitdark"){
    // Premium Split Dark — deep gradient
    const pH=Math.floor(H*pH_r),hH=H-pH;
    ctx.fillStyle="#060608";ctx.fillRect(0,0,W,H);
    dPhoto(0,0,W,pH);
    // Rich gradient overlay at bottom of photo
    const og=ctx.createLinearGradient(0,pH*0.45,0,pH);
    og.addColorStop(0,"rgba(6,6,8,0)");og.addColorStop(1,"rgba(6,6,8,1)");
    ctx.fillStyle=og;ctx.fillRect(0,pH*0.45,W,pH*0.55);
    dStrip(5);dLogo();
    ctx.fillStyle="#060608";ctx.fillRect(0,pH,W,hH);
    // Gold accent line
    const gl=ctx.createLinearGradient(0,0,W,0);
    gl.addColorStop(0,"rgba(212,165,32,0)");gl.addColorStop(0.5,"#D4A520");gl.addColorStop(1,"rgba(212,165,32,0)");
    ctx.fillStyle=gl;ctx.fillRect(0,pH,W,1.5*sc);
    const bH=dBadge(W/2,pH+PAD);
    dHL(W/2,pH+PAD+bH+6*sc,hH-PAD-bH-6*sc-FOOTER,"#f0f0f5");
    dGoldDiv(W/2,pH+hH-FOOTER);dSrc(W/2,pH+hH-FOOTER/2,"#444");

  }else if(template==="fullbleed"){
    // Premium Full Bleed — layered gradient
    dPhoto(0,0,W,H);
    // Multi-stop gradient for depth
    const g1=ctx.createLinearGradient(0,0,0,H*0.4);
    g1.addColorStop(0,"rgba(0,0,0,0.35)");g1.addColorStop(1,"rgba(0,0,0,0)");
    ctx.fillStyle=g1;ctx.fillRect(0,0,W,H*0.4);
    const g2=ctx.createLinearGradient(0,H*0.35,0,H);
    g2.addColorStop(0,"rgba(0,0,0,0)");g2.addColorStop(0.4,"rgba(0,0,0,0.75)");g2.addColorStop(1,"rgba(0,0,0,0.97)");
    ctx.fillStyle=g2;ctx.fillRect(0,H*0.35,W,H*0.65);
    dStrip(5);dLogo();
    // Side accent
    ctx.fillStyle=badgeColor||"#CC0000";ctx.fillRect(0,H*0.42,3*sc,H*0.58);
    const textZoneTop=H*0.55;
    const bH=dBadge(W/2,textZoneTop);
    dHL(W/2,textZoneTop+bH+8*sc,H-textZoneTop-bH-8*sc-FOOTER,"#fff");
    dDiv(W/2,H-FOOTER,"rgba(255,255,255,0.4)");dSrc(W/2,H-FOOTER/2,"rgba(255,255,255,0.55)");

  }else if(template==="gold"){
    // Premium Gold Elite — luxury dark + multi-stop gold
    ctx.fillStyle="#050505";ctx.fillRect(0,0,W,H);
    const pH=Math.floor(H*pH_r),hH=H-pH;
    dPhoto(0,0,W,pH);
    // Dark vignette on photo
    const gv=ctx.createRadialGradient(W/2,pH/2,pH*0.1,W/2,pH/2,pH*0.9);
    gv.addColorStop(0,"rgba(0,0,0,0)");gv.addColorStop(1,"rgba(0,0,0,0.5)");
    ctx.fillStyle=gv;ctx.fillRect(0,0,W,pH);
    dLogo();
    // Multi-stop gold bar
    const gb=ctx.createLinearGradient(0,0,W,0);
    gb.addColorStop(0,"#3d2a00");gb.addColorStop(0.2,"#8B6508");gb.addColorStop(0.4,"#D4A520");
    gb.addColorStop(0.5,"#F5D77A");gb.addColorStop(0.6,"#D4A520");gb.addColorStop(0.8,"#8B6508");gb.addColorStop(1,"#3d2a00");
    ctx.fillStyle=gb;ctx.fillRect(0,pH,W,5*sc);
    ctx.fillStyle="#050505";ctx.fillRect(0,pH+5*sc,W,hH-5*sc);
    // Subtle gold side lines
    ctx.fillStyle="rgba(212,165,32,0.3)";ctx.fillRect(0,pH+5*sc,1.5*sc,hH);ctx.fillRect(W-1.5*sc,pH+5*sc,1.5*sc,hH);
    const bH=dOutlineBadge(W/2,pH+PAD,"#D4A520");
    dHL(W/2,pH+PAD+bH+8*sc,hH-PAD-bH-8*sc-FOOTER,"#f0e8c0");
    dGoldDiv(W/2,pH+hH-FOOTER);dSrc(W/2,pH+hH-FOOTER/2,"#4a3800");

  }else if(template==="ticker"){
    // Premium Ticker — broadcast style
    ctx.fillStyle="#000";ctx.fillRect(0,0,W,H);
    const tkH=44*sc,srcH=28*sc,pH=H-tkH-srcH;
    dPhoto(0,0,W,pH);dStrip(5);dLogo();
    const tg=ctx.createLinearGradient(0,pH*0.45,0,pH);
    tg.addColorStop(0,"rgba(0,0,0,0)");tg.addColorStop(1,"rgba(0,0,0,0.96)");
    ctx.fillStyle=tg;ctx.fillRect(0,pH*0.45,W,pH*0.55);
    const hlH=getHL_H();
    const hlTop=pH-hlH-PAD-6*sc;
    drawColorHL(ctx,segs,W/2,hlTop,maxW,fsPx,"#fff");
    dTicker(pH,tkH);
    if(ovSource){
      const sg=ctx.createLinearGradient(0,pH+tkH,0,pH+tkH+srcH);
      sg.addColorStop(0,"#111");sg.addColorStop(1,"#000");
      ctx.fillStyle=sg;ctx.fillRect(0,pH+tkH,W,srcH);
      dSrc(W/2,pH+tkH+srcH/2,"rgba(255,255,255,0.4)");
    }

  }else if(template==="redbreak"){
    // Premium Red Break — bold impact
    const pH=Math.floor(H*pH_r),hH=H-pH;
    dPhoto(0,0,W,pH);
    // Top strip
    ctx.fillStyle=badgeColor||"#CC0000";ctx.fillRect(0,0,W,5*sc);
    // Shine
    ctx.save();ctx.globalAlpha=0.25;ctx.fillStyle="#fff";ctx.fillRect(0,0,W,2.5*sc);ctx.restore();
    dLogo();
    // Bottom box with gradient
    const rg=ctx.createLinearGradient(0,pH,0,pH+hH);
    rg.addColorStop(0,badgeColor||"#CC0000");rg.addColorStop(1,"#6B0000");
    ctx.fillStyle=rg;ctx.fillRect(0,pH,W,hH);
    // Diagonal pattern overlay
    ctx.save();ctx.globalAlpha=0.04;
    for(let i=-hH;i<W+hH;i+=20*sc){
      ctx.fillStyle="#fff";ctx.save();ctx.translate(i+pH,pH);ctx.rotate(Math.PI/4);
      ctx.fillRect(0,0,2*sc,hH*2);ctx.restore();
    }
    ctx.restore();
    const bH=dBadge(W/2,pH+PAD,true);
    dHL(W/2,pH+PAD+bH+6*sc,hH-PAD-bH-6*sc-FOOTER,"#fff");
    dDiv(W/2,pH+hH-FOOTER,"rgba(255,255,255,0.4)");
    dSrc(W/2,pH+hH-FOOTER/2,"rgba(255,255,255,0.65)");

  }else if(template==="darkdrama"){
    // Premium Dark Drama — moody cinematic
    ctx.fillStyle="#080808";ctx.fillRect(0,0,W,H);
    const pH=Math.floor(H*pH_r),hH=H-pH;
    dPhoto(0,0,W,pH);
    // Heavy dark overlay
    ctx.save();ctx.globalAlpha=0.45;ctx.fillStyle="#000";ctx.fillRect(0,0,W,pH);ctx.restore();
    const dg2=ctx.createLinearGradient(0,0,0,pH);
    dg2.addColorStop(0,"rgba(8,8,8,0.1)");dg2.addColorStop(0.6,"rgba(8,8,8,0)");dg2.addColorStop(1,"rgba(8,8,8,1)");
    ctx.fillStyle=dg2;ctx.fillRect(0,0,W,pH);
    dStrip(5);dLogo();
    ctx.fillStyle="#080808";ctx.fillRect(0,pH,W,hH);
    // Gold accent
    ctx.fillStyle="rgba(212,165,32,0.15)";ctx.fillRect(0,pH,W,1*sc);
    const bH=dOutlineBadge(W/2,pH+PAD,"#D4A520");
    dHL(W/2,pH+PAD+bH+6*sc,hH-PAD-bH-6*sc-FOOTER,"#e8e8e8");
    dGoldDiv(W/2,pH+hH-FOOTER);dSrc(W/2,pH+hH-FOOTER/2,"#3a3a3a");

  }else if(template==="newspaper"){
    // Premium Newspaper — editorial broadsheet
    ctx.fillStyle="#f2ede0";ctx.fillRect(0,0,W,H);
    // Aged paper texture
    ctx.save();ctx.globalAlpha=0.03;
    for(let i=0;i<H;i+=4){ctx.fillStyle=i%8===0?"#8B7355":"#a09070";ctx.fillRect(0,i,W,2);}
    ctx.restore();
    const hdrH=34*sc;
    // Header bar
    ctx.fillStyle="#1a1208";ctx.fillRect(0,0,W,hdrH);
    ctx.save();ctx.font=`800 ${15*sc}px Georgia,serif`;ctx.fillStyle="#f2ede0";
    ctx.textAlign="left";ctx.textBaseline="middle";ctx.fillText(source||"BBN NEWS",10*sc,hdrH/2);ctx.restore();
    ctx.save();ctx.font=`400 ${7*sc}px Trebuchet MS,sans-serif`;ctx.fillStyle="#888";
    ctx.textAlign="right";ctx.textBaseline="middle";
    const nd=new Date();ctx.fillText(`${nd.getDate()} ${MONTHS[nd.getMonth()]} ${nd.getFullYear()}`,W-10*sc,hdrH/2);ctx.restore();
    // Double rule
    ctx.fillStyle="#1a1208";ctx.fillRect(0,hdrH,W,2.5*sc);
    ctx.fillStyle="#c8b87a";ctx.fillRect(0,hdrH+4*sc,W,1*sc);
    ctx.fillStyle="#1a1208";ctx.fillRect(0,hdrH+6.5*sc,W,1.5*sc);
    const pH2=Math.floor((H-hdrH)*pH_r*0.85);
    dPhoto(0,hdrH+10*sc,W,pH2);
    let cY=hdrH+10*sc+pH2+10*sc;
    if(ovBadge){
      ctx.save();ctx.font=`800 ${7.5*sc}px Arial,sans-serif`;ctx.fillStyle="#CC0000";
      ctx.textAlign="left";ctx.textBaseline="top";
      ctx.fillText((badgeText||"Breaking").replace(/[^\w\s⚡•·]/g,"").trim().toUpperCase(),12*sc,cY);
      ctx.restore();cY+=14*sc;
    }
    // Rule under badge
    ctx.fillStyle="#c8b87a";ctx.fillRect(12*sc,cY,W-24*sc,1*sc);cY+=6*sc;
    ctx.save();ctx.font=`900 ${fsPx*0.82}px Georgia,serif`;ctx.fillStyle="#1a1208";
    ctx.textAlign="left";ctx.textBaseline="top";
    const plain=(headSegs||[]).map(s=>s.t).join("");
    const hw=plain.split(" ");let ln="",lns=[];
    for(let w of hw){const t=ln?ln+" "+w:w;if(ctx.measureText(t).width>W-24*sc&&ln){lns.push(ln);ln=w;}else ln=t;}
    if(ln)lns.push(ln);
    lns.forEach((l,i)=>ctx.fillText(l,12*sc,cY+i*fsPx*0.82*1.3));ctx.restore();
    cY+=lns.length*fsPx*0.82*1.3+8*sc;
    ctx.fillStyle="#c8b87a";ctx.fillRect(12*sc,cY,W-24*sc,1*sc);cY+=8*sc;
    if(ovSource){ctx.save();ctx.font=`400 ${7*sc}px Trebuchet MS,sans-serif`;ctx.fillStyle="#777";
      ctx.textAlign="left";ctx.textBaseline="top";
      ctx.fillText(`${(source||"BBN NEWS").toUpperCase()} • Special Report`,12*sc,cY);ctx.restore();}

  // ═══════════════════════════════════════════════════════════
  // 4 NEW PREMIUM TEMPLATES
  // ═══════════════════════════════════════════════════════════

  }else if(template==="cinematic"){
    // CINEMATIC — Widescreen letterbox film style
    const barH=44*sc; // top+bottom letterbox bars
    ctx.fillStyle="#000";ctx.fillRect(0,0,W,H);
    // Photo in middle zone
    const photoZoneY=barH,photoZoneH=H-barH*2;
    dPhoto(0,photoZoneY,W,photoZoneH);
    // Letterbox bars
    ctx.fillStyle="#000";ctx.fillRect(0,0,W,barH);ctx.fillRect(0,H-barH,W,barH);
    // Top bar — channel name
    ctx.save();
    ctx.font=`300 ${8*sc}px Trebuchet MS,sans-serif`;ctx.fillStyle="rgba(255,255,255,0.5)";
    ctx.textAlign="left";ctx.textBaseline="middle";
    ctx.fillText((source||"BBN NEWS").toUpperCase(),14*sc,barH/2);
    // Tiny dots
    ctx.fillStyle="#D4A520";ctx.beginPath();ctx.arc(W-20*sc,barH/2,2*sc,0,Math.PI*2);ctx.fill();
    ctx.fillStyle="rgba(212,165,32,0.5)";ctx.beginPath();ctx.arc(W-28*sc,barH/2,2*sc,0,Math.PI*2);ctx.fill();
    ctx.restore();
    // Gradient into bottom bar
    const cg=ctx.createLinearGradient(0,H-barH-40*sc,0,H-barH);
    cg.addColorStop(0,"rgba(0,0,0,0)");cg.addColorStop(1,"rgba(0,0,0,0.9)");
    ctx.fillStyle=cg;ctx.fillRect(0,H-barH-40*sc,W,40*sc);
    // Bottom bar — headline
    const bH2=dBadge(W*0.5,H-barH+6*sc);
    // Headline in bottom bar
    const plain2=(headSegs||[]).map(s=>s.t).join("");
    ctx.save();ctx.font=`700 ${fsPx*0.72}px Arial,sans-serif`;ctx.fillStyle="#fff";
    ctx.textAlign="center";ctx.textBaseline="top";
    const hw2=plain2.split(" ");let ln2="",lns2=[];
    for(let w of hw2){const t=ln2?ln2+" "+w:w;if(ctx.measureText(t).width>W-28*sc&&ln2){lns2.push(ln2);ln2=w;}else ln2=t;}
    if(ln2)lns2.push(ln2);
    const lineH2=fsPx*0.72*1.3;
    const startY2=H-barH+8*sc+bH2+4*sc;
    lns2.slice(0,2).forEach((l,i)=>ctx.fillText(l,W/2,startY2+i*lineH2));
    ctx.restore();
    dLogo();
    // Film grain corners
    ctx.save();ctx.globalAlpha=0.08;
    ctx.fillStyle="#fff";
    for(let i=0;i<30;i++){ctx.fillRect(Math.random()*W,Math.random()*H,1,1);}
    ctx.restore();

  }else if(template==="neonpulse"){
    // NEON PULSE — vibrant glow cyberpunk style
    ctx.fillStyle="#0a0012";ctx.fillRect(0,0,W,H);
    const pH=Math.floor(H*pH_r),hH=H-pH;
    dPhoto(0,0,W,pH);
    // Neon glow overlay on photo
    ctx.save();ctx.globalAlpha=0.25;
    const ng=ctx.createLinearGradient(0,0,W,pH);
    ng.addColorStop(0,"rgba(138,43,226,0.4)");ng.addColorStop(0.5,"rgba(0,191,255,0.1)");ng.addColorStop(1,"rgba(255,20,147,0.3)");
    ctx.fillStyle=ng;ctx.fillRect(0,0,W,pH);ctx.restore();
    // Bottom gradient
    const nbg=ctx.createLinearGradient(0,pH*0.6,0,pH);
    nbg.addColorStop(0,"rgba(10,0,18,0)");nbg.addColorStop(1,"rgba(10,0,18,1)");
    ctx.fillStyle=nbg;ctx.fillRect(0,pH*0.6,W,pH*0.4);
    dLogo();
    ctx.fillStyle="#0a0012";ctx.fillRect(0,pH,W,hH);
    // Neon top border — purple to cyan
    const nb=ctx.createLinearGradient(0,0,W,0);
    nb.addColorStop(0,"#8B2BE2");nb.addColorStop(0.5,"#00BFFF");nb.addColorStop(1,"#FF1493");
    ctx.fillStyle=nb;ctx.fillRect(0,pH,W,3*sc);
    // Glow effect on border
    ctx.save();ctx.shadowColor="#8B2BE2";ctx.shadowBlur=20*sc;
    ctx.fillStyle="rgba(139,43,226,0.6)";ctx.fillRect(0,pH,W,3*sc);ctx.restore();
    // Badge with neon style
    if(ovBadge){
      const txt=badgeText||"⚡ Breaking News";
      ctx.save();ctx.font=`700 ${9*sc}px Arial,sans-serif`;
      const bW=ctx.measureText(txt).width+24*sc,bH3=19*sc,bx=W/2-bW/2;
      ctx.strokeStyle="#00BFFF";ctx.lineWidth=1.5*sc;
      ctx.shadowColor="#00BFFF";ctx.shadowBlur=10*sc;
      ctx.beginPath();if(ctx.roundRect)ctx.roundRect(bx,pH+PAD,bW,bH3,4*sc);else ctx.rect(bx,pH+PAD,bW,bH3);
      ctx.stroke();ctx.fillStyle="#00BFFF";ctx.textAlign="center";ctx.textBaseline="middle";
      ctx.fillText(txt,W/2,pH+PAD+bH3/2);ctx.restore();
      const bH4=bH3;
      const hlH4=getHL_H();
      const topY4=pH+PAD+bH4+8*sc+Math.max(0,(hH-PAD-bH4-16*sc-FOOTER-hlH4)/2);
      drawColorHL(ctx,segs,W/2,topY4,maxW,fsPx,"#e0d0ff");
    } else {
      dHL(W/2,pH+PAD,hH-PAD*2-FOOTER,"#e0d0ff");
    }
    // Bottom neon line
    ctx.save();ctx.shadowColor="#FF1493";ctx.shadowBlur=15*sc;
    ctx.fillStyle="rgba(255,20,147,0.7)";ctx.fillRect(0,pH+hH-FOOTER-2*sc,W,1.5*sc);ctx.restore();
    dSrc(W/2,pH+hH-FOOTER/2,"rgba(139,43,226,0.8)");

  }else if(template==="magazine"){
    // MAGAZINE — editorial print style
    ctx.fillStyle="#fafafa";ctx.fillRect(0,0,W,H);
    const pH=Math.floor(H*pH_r*0.75),hH=H-pH;
    // Full width photo
    dPhoto(0,0,W,pH);
    // Category label on photo
    if(ovBadge){
      const cat=(badgeText||"Breaking News").toUpperCase();
      ctx.save();ctx.font=`800 ${8*sc}px Arial,sans-serif`;
      const cw=ctx.measureText(cat).width+20*sc;
      ctx.fillStyle=badgeColor||"#CC0000";
      ctx.fillRect(0,pH-26*sc,cw+10*sc,26*sc);
      ctx.fillStyle="#fff";ctx.textAlign="left";ctx.textBaseline="middle";
      ctx.fillText(cat,10*sc,pH-13*sc);ctx.restore();
    }
    dLogo();
    // White content area
    ctx.fillStyle="#fafafa";ctx.fillRect(0,pH,W,hH);
    // Left red accent bar
    ctx.fillStyle=badgeColor||"#CC0000";ctx.fillRect(0,pH,4*sc,hH);
    // Source label
    if(ovSource){
      ctx.save();ctx.font=`700 ${7*sc}px Trebuchet MS,sans-serif`;
      ctx.fillStyle=badgeColor||"#CC0000";ctx.textAlign="left";ctx.textBaseline="top";
      ctx.fillText((source||"BBN NEWS").toUpperCase(),14*sc,pH+10*sc);ctx.restore();
    }
    // Divider under source
    ctx.fillStyle="#e0e0e0";ctx.fillRect(14*sc,pH+22*sc,W-28*sc,1*sc);
    // Headline — large, left-aligned
    ctx.save();ctx.font=`900 ${fsPx*0.9}px Georgia,serif`;
    ctx.fillStyle=hlColor||"#111";ctx.textAlign="left";ctx.textBaseline="top";
    const pw2=(headSegs||[]).map(s=>s.t).join("");
    const mw2=W-24*sc;
    const mwds=pw2.split(" ");let mln="",mlns=[];
    for(let w of mwds){const t=mln?mln+" "+w:w;if(ctx.measureText(t).width>mw2&&mln){mlns.push(mln);mln=w;}else mln=t;}
    if(mln)mlns.push(mln);
    mlns.forEach((l,i)=>ctx.fillText(l,14*sc,pH+28*sc+i*fsPx*0.9*1.35));
    ctx.restore();
    // Bottom date
    ctx.save();ctx.font=`400 ${7*sc}px Trebuchet MS,sans-serif`;ctx.fillStyle="#aaa";
    ctx.textAlign="right";ctx.textBaseline="bottom";
    const nd2=new Date();ctx.fillText(`${nd2.getDate()} ${MONTHS[nd2.getMonth()]} ${nd2.getFullYear()}`,W-12*sc,H-8*sc);
    ctx.restore();

  }else if(template==="election"){
    // ELECTION — results board style
    ctx.fillStyle="#0a0a14";ctx.fillRect(0,0,W,H);
    // Subtle flag-color top strip
    const es=ctx.createLinearGradient(0,0,W,0);
    es.addColorStop(0,"#FF9933");es.addColorStop(0.33,"#FF9933");
    es.addColorStop(0.34,"#fff");es.addColorStop(0.66,"#fff");
    es.addColorStop(0.67,"#138808");es.addColorStop(1,"#138808");
    ctx.fillStyle=es;ctx.fillRect(0,0,W,6*sc);
    // Photo — top 45%
    const epH=Math.floor(H*0.45);
    dPhoto(0,6*sc,W,epH-6*sc);
    // Dark overlay on photo
    ctx.save();ctx.globalAlpha=0.5;ctx.fillStyle="#000";ctx.fillRect(0,6*sc,W,epH-6*sc);ctx.restore();
    dLogo();
    // Results bar
    const rbY=epH,rbH=36*sc;
    ctx.fillStyle="#1a1a2e";ctx.fillRect(0,rbY,W,rbH);
    ctx.fillStyle="#CC0000";ctx.fillRect(0,rbY,W,3*sc);
    // LIVE RESULTS text
    if(ovLive){
      ctx.save();ctx.font=`800 ${8*sc}px Arial,sans-serif`;
      ctx.fillStyle="#CC0000";ctx.textAlign="left";ctx.textBaseline="middle";
      ctx.fillText("●",10*sc,rbY+rbH/2);
      ctx.fillStyle="#fff";ctx.fillText("LIVE RESULTS",20*sc,rbY+rbH/2);ctx.restore();
    }
    if(ovSource){
      ctx.save();ctx.font=`600 ${7*sc}px Arial,sans-serif`;ctx.fillStyle="#888";
      ctx.textAlign="right";ctx.textBaseline="middle";
      ctx.fillText((source||"BBN NEWS").toUpperCase(),W-10*sc,rbY+rbH/2);ctx.restore();
    }
    // Main content
    const cY2=rbY+rbH+10*sc;
    if(ovBadge){
      const et=badgeText||"⚡ Breaking";
      ctx.save();ctx.font=`700 ${8.5*sc}px Arial,sans-serif`;
      const ew=ctx.measureText(et).width+18*sc;
      ctx.fillStyle="#CC0000";ctx.beginPath();if(ctx.roundRect)ctx.roundRect(W/2-ew/2,cY2,ew,17*sc,3*sc);else ctx.rect(W/2-ew/2,cY2,ew,17*sc);
      ctx.fill();ctx.fillStyle="#fff";ctx.textAlign="center";ctx.textBaseline="middle";
      ctx.fillText(et,W/2,cY2+8.5*sc);ctx.restore();
    }
    const eZoneTop=cY2+(ovBadge?22*sc:0);
    const eZoneH=H-eZoneTop-20*sc;
    dHL(W/2,eZoneTop,eZoneH,"#fff");
    // Bottom border
    ctx.fillStyle=es;ctx.fillRect(0,H-4*sc,W,4*sc);
  }
}

// ══════════════════════════════════════════════════════════════
// MAIN COMPONENT (unchanged from original)
// ══════════════════════════════════════════════════════════════
export default function PhotoNewsmaker({ user = null, globalLogo = null }){
  const [step,      setStep]      = useState("template");
  const [template,  setTemplate]  = useState("classic");
  const [headHTML,  setHeadHTML]  = useState(SAMPLE_CAPTION);
  const [canvasFont,  setCanvasFont]  = useState("Arial");
  const [canvasColor, setCanvasColor] = useState("#111111");
  const [canvasBold,  setCanvasBold]  = useState(true);
  const [canvasItalic,setCanvasItalic]= useState(false);
  const [source,    setSource]    = useState("BBN NEWS");
  const [badgeText, setBadgeText] = useState("⚡ Breaking News");
  const [liveLabel, setLiveLabel] = useState("LIVE");
  const [badgeColor,setBadgeColor]= useState("#CC0000");
  const [stripColor,setStripColor]= useState("#CC0000");
  const [boxBg,     setBoxBg]     = useState("#ffffff");
  const [hlColor,   setHlColor]   = useState("#111111");
  const [tickerColor,setTickerColor]=useState("#CC0000");
  const [fontSize,  setFontSize]  = useState(5.5);
  const [ovLogo,    setOvLogo]    = useState(true);
  const [ovLive,    setOvLive]    = useState(true);
  const [ovBadge,   setOvBadge]   = useState(true);
  const [ovStrip,   setOvStrip]   = useState(true);
  const [ovDivider, setOvDivider] = useState(true);
  const [ovSource,  setOvSource]  = useState(true);
  const [photoPos,  setPhotoPos]  = useState(0.2);
  const [photoH,    setPhotoH]    = useState(50);
  const [brightness,setBrightness]= useState(100);
  const [contrast,  setContrast]  = useState(100);
  const [saturation,setSaturation]= useState(100);
  const [blur,      setBlur]      = useState(0);
  const [vignette,  setVignette]  = useState(0);
  const [warmth,    setWarmth]    = useState(0);
  const [photoFilter,setPhotoFilter]=useState("none");
  const [photoPrev, setPhotoPrev] = useState(null);
  const [logoSize,  setLogoSize]  = useState(10);
  const [logoPosKey,setLogoPosKey]= useState("tr");
  const [logoBorderColor,setLogoBorderColor]=useState("#D4A520");
  const [logoBorderWidth,setLogoBorderWidth]=useState(1.5);
  const [logoShape, setLogoShape] = useState("circle");
  const [logoPrev,  setLogoPrev]  = useState(null);
  const [selColor,  setSelColor]  = useState("#CC0000");
  // eslint-disable-next-line no-unused-vars
  const [isBold,    setIsBold]    = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isItalic,  setIsItalic]  = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [rawInput,  setRawInput]  = useState("");
  const [showTranslate,setShowTranslate]=useState(false);
  const [translating,setTranslating]=useState(false);
  const [noSelWarn,setNoSelWarn]=useState(false);
  const [showPreview,setShowPreview]=useState(false);
  const [photoUploading,setPhotoUploading]=useState(false);
  const [photoUploadPct,setPhotoUploadPct]=useState(0);
  const [logoUploading,setLogoUploading]=useState(false);
  const [photoCloudUrl,setPhotoCloudUrl]=useState(null);
  const [logoCloudUrl,setLogoCloudUrl]=useState(null);

  const canvasRef      = useRef(null);
  const photoRef       = useRef(null);
  const logoRef        = useRef(null);
  const hlBoxRef       = useRef(null);
  const lastSetHTML    = useRef(SAMPLE_CAPTION); // init with sample caption

  // eslint-disable-next-line no-unused-vars
  const headSegs = parseSegs(headHTML);

  // eslint-disable-next-line no-unused-vars
  function getPlainText(){
    const b = hlBoxRef.current;
    if(b) return (b.innerText || b.textContent || "").trim() || "Write your headline here";
    return headHTML.replace(/<[^>]+>/g,"").replace(/&nbsp;/g," ").trim() || "Write your headline here";
  }

  // ── Auto-load globalLogo from App if no local logo set ────────
  useEffect(() => {
    if (!globalLogo?.url) return;
    if (logoPrev) return; // already has a local logo — don't override
    const url = globalLogo.url;
    setLogoCloudUrl(url);
    setLogoPrev(url);
    const img = new Image(); img.crossOrigin = "anonymous";
    img.onload = () => { logoRef.current = img; redraw(); };
    img.onerror = () => {
      if (globalLogo.dataUrl) {
        const img2 = new Image();
        img2.onload = () => { logoRef.current = img2; setLogoPrev(globalLogo.dataUrl); redraw(); };
        img2.src = globalLogo.dataUrl;
      }
    };
    img.src = url;
  // eslint-disable-next-line
  }, [globalLogo]);

  // ── Load sample/demo image on first open (no saved photo) ────
  useEffect(() => {
    const saved = lsLoad();
    if (saved?.photoCloudUrl || saved?.photoPrev) return;
    // Load sample image
    const img = new Image();
    img.onload = () => {
      photoRef.current = img;
      setPhotoPrev(SAMPLE_NEWS_IMAGE);
      redraw();
    };
    img.src = SAMPLE_NEWS_IMAGE;
    // Set headline — state already initialized with SAMPLE_CAPTION
    // But also sync to DOM box if already mounted
    if (hlBoxRef.current) {
      hlBoxRef.current.innerHTML = SAMPLE_CAPTION;
      lastSetHTML.current = SAMPLE_CAPTION;
    }
  // eslint-disable-next-line
  }, []);

  // ── Auto-set text color based on template background ─────────
  useEffect(()=>{
    const darkTemplates = ["splitdark","fullbleed","gold","ticker","redbreak","darkdrama","cinematic","neonpulse","election"];
    const lightTemplates = ["classic","newspaper","magazine"];
    if(darkTemplates.includes(template)){
      setCanvasColor("#ffffff");
      setHlColor("#ffffff");
    } else if(lightTemplates.includes(template)){
      setCanvasColor("#111111");
      setHlColor("#111111");
    }
  // eslint-disable-next-line
  },[template]);

  const redraw = useCallback(()=>{
    const c=canvasRef.current;if(!c)return;
    const plain = hlBoxRef.current
      ? (hlBoxRef.current.innerText||hlBoxRef.current.textContent||"").trim()
      : headHTML.replace(/<[^>]+>/g,"").replace(/&nbsp;/g," ").trim();
    const simpleSegs = [{
      t: plain || "Write your headline here",
      c: canvasColor,
      b: canvasBold,
      i: canvasItalic,
      f: canvasFont !== "Arial" ? canvasFont : null,
      sz: null
    }];
    drawCard(c,photoRef.current,logoRef.current,{
      template, headSegs:simpleSegs, source, badgeText, liveLabel,
      badgeColor, stripColor, boxBg, hlColor, tickerColor,
      ovLogo, ovLive, ovBadge, ovStrip, ovDivider, ovSource,
      fontSize, logoSize, logoPosKey, logoBorderColor, logoBorderWidth, logoShape,
      photoPos, photoH, brightness, contrast, saturation, blur, vignette, warmth, photoFilter
    });
  // eslint-disable-next-line
  },[template,headHTML,source,badgeText,liveLabel,badgeColor,stripColor,boxBg,hlColor,
     tickerColor,ovLogo,ovLive,ovBadge,ovStrip,ovDivider,ovSource,fontSize,logoSize,
     logoPosKey,logoBorderColor,logoBorderWidth,logoShape,photoPos,photoH,
     brightness,contrast,saturation,blur,vignette,warmth,photoFilter,
     canvasFont,canvasColor,canvasBold,canvasItalic]);

  useEffect(()=>{redraw();},[redraw]);

  function loadPhotoSrc(src){
    const img=new Image();img.crossOrigin="anonymous";
    img.onload=()=>{photoRef.current=img;redraw();};
    img.onerror=()=>{const i=new Image();i.onload=()=>{photoRef.current=i;redraw();};i.src=src;};
    img.src=src;
  }

  // ── Photo upload → Cloudinary ──────────────────────────────
  async function handlePhotoFile(e){
    const f=e.target.files[0]; if(!f) return;
    // Show local preview instantly
    const r=new FileReader();
    r.onload=ev=>{ setPhotoPrev(ev.target.result); loadPhotoSrc(ev.target.result); };
    r.readAsDataURL(f);
    // Upload to Cloudinary in background
    setPhotoUploading(true); setPhotoUploadPct(0);
    try {
      const url = await uploadToCloudinary(f, pct=>setPhotoUploadPct(pct));
      setPhotoCloudUrl(url);
      loadPhotoSrc(url); // reload from Cloudinary (original quality)
      setPhotoPrev(url);
    } catch(err) { console.warn("Cloudinary photo upload failed:", err.message); }
    setPhotoUploading(false);
  }

  // ── Logo upload → Cloudinary ───────────────────────────────
  async function handleLogoFile(e){
    const f=e.target.files[0]; if(!f) return;
    // Show local preview instantly
    const r=new FileReader();
    r.onload=ev=>{
      const img=new Image();
      img.onload=()=>{ logoRef.current=img; setLogoPrev(ev.target.result); redraw(); };
      img.src=ev.target.result;
    };
    r.readAsDataURL(f);
    // Upload to Cloudinary
    setLogoUploading(true);
    try {
      const url = await uploadToCloudinary(f, null);
      setLogoCloudUrl(url);
      const img=new Image(); img.crossOrigin="anonymous";
      img.onload=()=>{ logoRef.current=img; setLogoPrev(url); redraw(); };
      img.src=url;
    } catch(err) { console.warn("Cloudinary logo upload failed:", err.message); }
    setLogoUploading(false);
  }

  // ── Save to localStorage + Firestore on every change ────────
  useEffect(()=>{
    const data = {
      template,headHTML,source,badgeText,liveLabel,
      badgeColor,stripColor,boxBg,hlColor,tickerColor,fontSize,
      ovLogo,ovLive,ovBadge,ovStrip,ovDivider,ovSource,
      photoPos,photoH,brightness,contrast,saturation,blur,vignette,warmth,photoFilter,
      logoSize,logoPosKey,logoBorderColor,logoBorderWidth,logoShape,
      canvasFont,canvasColor,canvasBold,canvasItalic,
      photoCloudUrl,logoCloudUrl,
    };
    // Always save to localStorage (offline backup)
    lsSave(data);
    // If logged in — save to Firestore too (cross-device sync)
    if(user?.uid) {
      saveUserData(user.uid, { photoNewsmaker: data });
    }
  // eslint-disable-next-line
  },[template,headHTML,source,badgeText,liveLabel,badgeColor,stripColor,boxBg,hlColor,
     tickerColor,fontSize,ovLogo,ovLive,ovBadge,ovStrip,ovDivider,ovSource,
     photoPos,photoH,brightness,contrast,saturation,blur,vignette,warmth,photoFilter,
     logoSize,logoPosKey,logoBorderColor,logoBorderWidth,logoShape,
     canvasFont,canvasColor,canvasBold,canvasItalic,photoCloudUrl,logoCloudUrl,user]);

  // ── Restore from Firestore (if logged in) else localStorage ──
  useEffect(()=>{
    async function loadData() {
      let d = null;
      if(user?.uid) {
        const fireData = await loadUserData(user.uid);
        d = fireData?.photoNewsmaker || null;
      }
      if(!d) d = lsLoad(); // fallback to localStorage
      if(!d) return;
    if(d.template)       setTemplate(d.template);
    if(d.headHTML)       setHeadHTML(d.headHTML);
    if(d.source)         setSource(d.source);
    if(d.badgeText)      setBadgeText(d.badgeText);
    if(d.liveLabel)      setLiveLabel(d.liveLabel);
    if(d.badgeColor)     setBadgeColor(d.badgeColor);
    if(d.stripColor)     setStripColor(d.stripColor);
    if(d.boxBg)          setBoxBg(d.boxBg);
    if(d.hlColor)        setHlColor(d.hlColor);
    if(d.tickerColor)    setTickerColor(d.tickerColor);
    if(d.fontSize)       setFontSize(d.fontSize);
    if(d.ovLogo!=null)   setOvLogo(d.ovLogo);
    if(d.ovLive!=null)   setOvLive(d.ovLive);
    if(d.ovBadge!=null)  setOvBadge(d.ovBadge);
    if(d.ovStrip!=null)  setOvStrip(d.ovStrip);
    if(d.ovDivider!=null)setOvDivider(d.ovDivider);
    if(d.ovSource!=null) setOvSource(d.ovSource);
    if(d.photoPos)       setPhotoPos(d.photoPos);
    if(d.photoH)         setPhotoH(d.photoH);
    if(d.brightness)     setBrightness(d.brightness);
    if(d.contrast)       setContrast(d.contrast);
    if(d.saturation)     setSaturation(d.saturation);
    if(d.blur!=null)     setBlur(d.blur);
    if(d.vignette!=null) setVignette(d.vignette);
    if(d.warmth!=null)   setWarmth(d.warmth);
    if(d.photoFilter)    setPhotoFilter(d.photoFilter);
    if(d.logoSize)       setLogoSize(d.logoSize);
    if(d.logoPosKey)     setLogoPosKey(d.logoPosKey);
    if(d.logoBorderColor)setLogoBorderColor(d.logoBorderColor);
    if(d.logoBorderWidth)setLogoBorderWidth(d.logoBorderWidth);
    if(d.logoShape)      setLogoShape(d.logoShape);
    if(d.canvasFont)     setCanvasFont(d.canvasFont);
    if(d.canvasColor)    setCanvasColor(d.canvasColor);
    if(d.canvasBold!=null)  setCanvasBold(d.canvasBold);
    if(d.canvasItalic!=null)setCanvasItalic(d.canvasItalic);
    // Restore photos from Cloudinary URLs
    if(d.photoCloudUrl){
      setPhotoCloudUrl(d.photoCloudUrl);
      setPhotoPrev(d.photoCloudUrl);
      loadPhotoSrc(d.photoCloudUrl);
    } else {
      // No saved photo → load sample image
      const img = new Image();
      img.onload = () => { photoRef.current = img; redraw(); };
      img.src = SAMPLE_NEWS_IMAGE;
      setPhotoPrev(SAMPLE_NEWS_IMAGE);
    }
    if(d.logoCloudUrl){
      setLogoCloudUrl(d.logoCloudUrl);
      setLogoPrev(d.logoCloudUrl);
      const img=new Image(); img.crossOrigin="anonymous";
      img.onload=()=>{ logoRef.current=img; redraw(); };
      img.src=d.logoCloudUrl;
    }
    }
    loadData();
  // eslint-disable-next-line
  },[user]);
  useEffect(()=>{
    const b = hlBoxRef.current;
    if(!b) return;
    if(headHTML !== lastSetHTML.current){
      b.innerHTML = headHTML;
      lastSetHTML.current = headHTML;
    }
    // On first mount — if box is empty, fill with current headHTML (sample caption)
    if(!b.innerHTML || b.innerHTML === "<br>"){
      b.innerHTML = headHTML;
      lastSetHTML.current = headHTML;
    }
  },[headHTML]);

  async function doTranslate(lang){
    const b = hlBoxRef.current;
    const textToTranslate = b ? (b.innerText||b.textContent||"").trim() : "";
    if(!textToTranslate) return;
    setTranslating(true);
    try{
      const txt = await translateText(textToTranslate, lang);
      if(hlBoxRef.current){ hlBoxRef.current.innerText = txt; }
      setHeadHTML(txt);
      lastSetHTML.current = txt;
      setShowTranslate(false);
    }catch(e){
      alert("Translate failed. Check internet connection.");
    }
    setTranslating(false);
  }

  // ── Save current selection (range) before button click loses focus ──
  const savedRangeRef = useRef(null);

  function saveSelection(){
    const sel = window.getSelection();
    if(sel && sel.rangeCount > 0){
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  }

  function restoreSelection(){
    const b = hlBoxRef.current; if(!b) return false;
    b.focus();
    const range = savedRangeRef.current;
    if(!range) return false;
    try {
      const sel = window.getSelection();
      if(sel){ sel.removeAllRanges(); sel.addRange(range); }
      return true;
    } catch(e){ return false; }
  }

  function hasSelection(){
    const range = savedRangeRef.current;
    if(!range) return false;
    return range.toString().length > 0;
  }

  function execCmd(cmd, value=null){
    const b = hlBoxRef.current; if(!b) return;
    if(!hasSelection()){
      setNoSelWarn(true);
      setTimeout(()=>setNoSelWarn(false), 2500);
      return;
    }
    restoreSelection();
    try { document.execCommand(cmd, false, value); } catch(e) {}
    const html = b.innerHTML;
    lastSetHTML.current = html;
    setHeadHTML(html);
    // Re-save selection after command (selection may have changed)
    const sel = window.getSelection();
    if(sel && sel.rangeCount > 0) savedRangeRef.current = sel.getRangeAt(0).cloneRange();
  }

  function resetColors(){
    const b = hlBoxRef.current; if(!b) return;
    restoreSelection();
    if(hasSelection()){
      // Only remove format from selected text
      try { document.execCommand("removeFormat", false, null); } catch(e){}
      const html = b.innerHTML;
      lastSetHTML.current = html;
      setHeadHTML(html);
      savedRangeRef.current = null;
    } else {
      // No selection — clear ALL formatting (strip all HTML tags)
      const plain = b.innerText || b.textContent || "";
      b.innerHTML = plain;
      lastSetHTML.current = plain;
      setHeadHTML(plain);
    }
  }

  function resetLogo(){logoRef.current=null;setLogoPrev(null);redraw();}
  function doneText(){setStep("logo");}

  function downloadPNG(){
    const c=canvasRef.current;if(!c)return;
    const a=document.createElement("a");a.download=`BBN_${template}_${Date.now()}.png`;
    a.href=c.toDataURL("image/png");document.body.appendChild(a);a.click();document.body.removeChild(a);
  }

  const V={
    hdr:{fontSize:10,fontWeight:700,letterSpacing:"1.5px",color:"#D4A520",textTransform:"uppercase",marginBottom:8,display:"flex",alignItems:"center",gap:6},
    sub:{fontSize:10,color:"var(--txt-lo)",fontWeight:600,marginBottom:4,marginTop:8},
    row:{display:"flex",alignItems:"center",gap:7,marginBottom:5},
    snm:{fontSize:10,color:"var(--txt-lo)",minWidth:70,flexShrink:0},
    svl:{fontSize:10,color:"var(--txt-md)",minWidth:36,textAlign:"right",fontFamily:"monospace"},
    swRow:{display:"flex",flexWrap:"wrap",gap:4,marginTop:4},
    togRow:{display:"flex",alignItems:"center",gap:8,marginBottom:6},
    togLbl:{flex:1,fontSize:11,color:"var(--txt-md)"},
    nextBtn:{width:"100%",height:38,background:"linear-gradient(135deg,#CC0000,#880000)",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:800,letterSpacing:1,cursor:"pointer",marginTop:10},
    card:{background:"var(--bg-card)",border:"1px solid var(--border)",borderRadius:6,padding:10,marginBottom:8},
  };

  // ── Smooth Range Slider ──────────────────────────────────────
  function Sl({label, value, setter, min, max, unit="", step=1}){
    return (
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
        <span style={{fontSize:10,color:"var(--txt-lo)",minWidth:72,flexShrink:0,fontWeight:600}}>{label}</span>
        <input
          type="range" min={min} max={max} step={step} value={value}
          onChange={e=>setter(Number(e.target.value))}
          style={{flex:1,accentColor:"#CC0000",cursor:"pointer"}}
        />
        <span style={{fontSize:10,color:"var(--txt-md)",minWidth:36,textAlign:"right",fontFamily:"monospace"}}>{value}{unit}</span>
      </div>
    );
  }
  function Tog({on,fn,lbl}){
    return<div style={V.togRow}><span style={V.togLbl}>{lbl}</span><button className={`toggle-sw ${on?"on":""}`} onClick={fn}/></div>;
  }
  function Sw({colors,sel,fn,size=20}){
    return<div style={V.swRow}>{colors.map(c=>(
      <div key={c} onClick={()=>fn(c)} style={{width:size,height:size,borderRadius:4,cursor:"pointer",background:c,flexShrink:0,
        border:sel===c?"2.5px solid #fff":"1.5px solid rgba(255,255,255,0.15)",
        transform:sel===c?"scale(1.2)":"scale(1)",transition:"all 0.12s"}}/>
    ))}</div>;
  }
  function SecHdr({icon,label}){
    return<div style={V.hdr}><span style={{fontSize:16}}>{icon}</span>{label}<span style={{flex:1,height:1,background:"rgba(212,165,32,0.3)"}}/></div>;
  }

  // ── Nav buttons row: Back + Preview ──
  function NavRow({backStep,backLabel="← Back"}){
    return(
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        <button onClick={()=>setStep(backStep)} style={{
          flex:1,height:36,borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",
          background:"transparent",border:"1px solid var(--border-hi)",color:"var(--txt-md)"
        }}>{backLabel}</button>
        <button onClick={()=>setShowPreview(true)} style={{
          flex:1,height:36,borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",
          background:"rgba(212,165,32,0.1)",border:"1px solid var(--accent)",color:"var(--accent)"
        }}>👁 Preview</button>
      </div>
    );
  }

  // ── Full screen preview modal ──
  function PreviewModal(){
    if(!showPreview) return null;
    return(
      <div onClick={()=>setShowPreview(false)} style={{
        position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",zIndex:9999,
        display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14
      }}>
        <div style={{fontSize:11,color:"#888",letterSpacing:1}}>TAP ANYWHERE TO CLOSE</div>
        <canvas
          ref={el=>{
            if(!el) return;
            const src = canvasRef.current;
            if(!src) return;
            el.width=src.width; el.height=src.height;
            el.getContext("2d").drawImage(src,0,0);
          }}
          style={{maxWidth:"92vw",maxHeight:"72vh",borderRadius:6,boxShadow:"0 8px 48px rgba(0,0,0,0.9)"}}
        />
        <div style={{display:"flex",gap:10}}>
          <button onClick={e=>{e.stopPropagation();downloadPNG();setShowPreview(false);}} style={{
            height:44,padding:"0 28px",background:"linear-gradient(135deg,#CC0000,#880000)",
            color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:800,cursor:"pointer"
          }}>⬇️ Download PNG</button>
          <button onClick={()=>setShowPreview(false)} style={{
            height:44,padding:"0 20px",background:"transparent",
            color:"#888",border:"1px solid #444",borderRadius:8,fontSize:13,cursor:"pointer"
          }}>Close</button>
        </div>
      </div>
    );
  }

  const activeTpl=TEMPLATES.find(t=>t.id===template)||TEMPLATES[0];

  function renderStepContent(){
    if(step==="template") return(
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <button onClick={()=>{
            if(window.confirm("Sab saved data clear karna hai?")){
              localStorage.removeItem("bbn_pnm_v1");
              window.location.reload();
            }
          }} style={{
            height:30,padding:"0 10px",borderRadius:5,fontSize:10,fontWeight:700,cursor:"pointer",
            background:"transparent",border:"1px solid rgba(204,0,0,0.4)",color:"#CC0000"
          }}>🗑 Clear Saved</button>
          <button onClick={()=>setShowPreview(true)} style={{
            height:34,padding:"0 16px",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",
            background:"rgba(212,165,32,0.1)",border:"1px solid var(--accent)",color:"var(--accent)"
          }}>👁 Preview</button>
        </div>
        <SecHdr icon="🎨" label="Select Template"/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:12}}>
          {TEMPLATES.map(t=>(
            <button key={t.id} onClick={()=>setTemplate(t.id)} style={{
              padding:"10px 6px",borderRadius:7,cursor:"pointer",textAlign:"center",
              background:template===t.id?"rgba(204,34,0,0.18)":"var(--bg-card)",
              border:template===t.id?"2px solid var(--red)":"1px solid var(--border)",
              transition:"all 0.15s",display:"flex",flexDirection:"column",alignItems:"center",gap:4
            }}>
              <span style={{fontSize:22}}>{t.emoji}</span>
              <span style={{fontSize:11,fontWeight:700,color:template===t.id?"#fff":"var(--txt-md)"}}>{t.label}</span>
              <span style={{fontSize:10,color:"var(--txt-lo)"}}>{t.desc}</span>
            </button>
          ))}
        </div>
        <SecHdr icon="✏️" label="Edit Template"/>
        <div style={V.card}>
          <div style={{...V.sub,marginTop:0}}>Photo / News Split</div>
          <Sl label="Photo Height" value={photoH} setter={setPhotoH} min={25} max={75} unit="%"/>
          <div style={{...V.sub}}>Headline Size</div>
          <Sl label="Font Size" value={fontSize} setter={setFontSize} min={3} max={9} step={0.5}/>
          <div style={{...V.sub}}>Badge Color</div>
          <Sw colors={SWATCHES_BADGE} sel={badgeColor} fn={setBadgeColor}/>
          <div style={{...V.sub}}>Strip Color</div>
          <Sw colors={SWATCHES_STRIP} sel={stripColor} fn={setStripColor}/>
          <div style={{...V.sub}}>Ticker Bar</div>
          <Sw colors={SWATCHES_TICKER} sel={tickerColor} fn={setTickerColor}/>
          <div style={{...V.sub}}>News Box Background</div>
          <div style={{...V.swRow}}>
            {BOXBG.map(([c,fg,label])=>(
              <button key={c} onClick={()=>setBoxBg(c)} style={{
                padding:"3px 7px",borderRadius:4,fontSize:10,fontWeight:700,cursor:"pointer",
                background:c,color:fg,border:boxBg===c?"2.5px solid #fff":"1.5px solid rgba(255,255,255,0.15)",transition:"all 0.12s"
              }}>{label}</button>
            ))}
          </div>
          <div style={{...V.sub}}>Headline Default Color</div>
          <div style={{...V.swRow}}>
            {HLCOL.map(([c,fg,label])=>(
              <button key={c} onClick={()=>setHlColor(c)} style={{
                padding:"3px 8px",borderRadius:4,fontSize:10,fontWeight:700,cursor:"pointer",
                background:c,color:fg,border:hlColor===c?"2.5px solid var(--accent)":"1.5px solid rgba(255,255,255,0.15)",transition:"all 0.12s"
              }}>{label}</button>
            ))}
          </div>
        </div>
        <SecHdr icon="🏷️" label="Layers On/Off"/>
        <div style={V.card}>
          <Tog on={ovLogo}    fn={()=>setOvLogo(v=>!v)}    lbl="Logo Badge"/>
          <Tog on={ovLive}    fn={()=>setOvLive(v=>!v)}    lbl="LIVE Badge"/>
          <Tog on={ovBadge}   fn={()=>setOvBadge(v=>!v)}   lbl="Breaking Badge"/>
          <Tog on={ovStrip}   fn={()=>setOvStrip(v=>!v)}   lbl="Top Strip"/>
          <Tog on={ovDivider} fn={()=>setOvDivider(v=>!v)} lbl="Divider Line"/>
          <Tog on={ovSource}  fn={()=>setOvSource(v=>!v)}  lbl="Source Text"/>
        </div>
        <button style={V.nextBtn} onClick={()=>setStep("photo")}>Next: Add Photo →</button>
      </div>
    );

    if(step==="photo") return(
      <div>
        <NavRow backStep="template" backLabel="← Template"/>
        <SecHdr icon="📸" label="Upload Photo"/>
        <label style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,border:"2px dashed var(--border-hi)",borderRadius:8,padding:"16px 10px",cursor:"pointer",background:"var(--bg-card)",position:"relative",overflow:"hidden",marginBottom:8}}>
          <input type="file" accept="image/*" hidden onChange={handlePhotoFile}/>
          <span style={{fontSize:30}}>{photoUploading?"⏳":"🖼️"}</span>
          <span style={{fontSize:12,fontWeight:700,color:"var(--txt-md)"}}>
            {photoUploading?`Uploading... ${photoUploadPct}%`:"TAP TO UPLOAD PHOTO"}
          </span>
          <span style={{fontSize:10,color:"var(--txt-lo)"}}>
            {photoCloudUrl?"☁️ Saved on Cloudinary":"JPG · PNG · WEBP · Any size"}
          </span>
          {photoUploading&&<div style={{width:"100%",height:3,background:"var(--border)",borderRadius:2,marginTop:4}}>
            <div style={{width:`${photoUploadPct}%`,height:"100%",background:"var(--accent)",borderRadius:2,transition:"width 0.2s"}}/>
          </div>}
        </label>
        {photoPrev&&<img src={photoPrev} alt="" style={{width:"100%",height:80,objectFit:"cover",borderRadius:6,marginBottom:10,border:"1px solid var(--border)"}}/>}

        {/* Sample photo button */}
        <button onClick={()=>{
          const img=new Image();
          img.onload=()=>{photoRef.current=img;setPhotoPrev(SAMPLE_NEWS_IMAGE);setPhotoCloudUrl(null);redraw();};
          img.src=SAMPLE_NEWS_IMAGE;
          setHeadHTML(SAMPLE_CAPTION);
        }} style={{
          width:"100%",height:32,borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",
          background:"rgba(212,165,32,0.08)",border:"1px solid rgba(212,165,32,0.3)",
          color:"#D4A520",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"center",gap:6
        }}>🖼️ Sample Photo Reload Karo (Delhi Rain)</button>
        <div style={{...V.sub,marginTop:0}}>Or enter photo URL</div>
        <input className="control-input" placeholder="https://example.com/photo.jpg" style={{marginBottom:10}}
          onBlur={e=>{if(e.target.value.trim()){setPhotoPrev(e.target.value.trim());loadPhotoSrc(e.target.value.trim());}}}/>
        <SecHdr icon="🔍" label="Photo Position"/>
        <div style={V.card}>
          <Sl label="Vertical pos" value={Math.round(photoPos*100)} setter={v=>setPhotoPos(v/100)} min={0} max={100} unit="%"/>
        </div>
        <SecHdr icon="✨" label="Photo Effects"/>
        <div style={V.card}>
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:6}}>
            <button onClick={()=>{setBrightness(100);setContrast(100);setSaturation(100);setBlur(0);setVignette(0);setWarmth(0);setPhotoFilter("none");}}
              style={{fontSize:10,color:"var(--txt-lo)",background:"transparent",border:"1px solid var(--border)",borderRadius:4,padding:"2px 8px",cursor:"pointer"}}>Reset All</button>
          </div>
          <Sl label="Brightness" value={brightness} setter={setBrightness} min={20} max={160}/>
          <Sl label="Contrast"   value={contrast}   setter={setContrast}   min={50} max={200}/>
          <Sl label="Saturation" value={saturation} setter={setSaturation} min={0}  max={200}/>
          <Sl label="Blur"       value={blur}        setter={setBlur}       min={0}  max={10} step={0.5} unit="px"/>
          <Sl label="Vignette"   value={vignette}    setter={setVignette}   min={0}  max={90} unit="%"/>
          <Sl label="Warmth"     value={warmth}      setter={setWarmth}     min={-80} max={80}/>
        </div>
        <SecHdr icon="🎞️" label="Preset Filters"/>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:4,marginBottom:12}}>
          {PHOTO_FILTERS.map(({id,label})=>(
            <button key={id} onClick={()=>setPhotoFilter(id)} style={{
              padding:"6px 2px",borderRadius:5,fontSize:10,fontWeight:700,cursor:"pointer",textAlign:"center",
              background:photoFilter===id?"rgba(232,160,32,0.2)":"var(--bg-card)",
              color:photoFilter===id?"var(--accent)":"var(--txt-lo)",
              border:photoFilter===id?"1.5px solid var(--accent)":"1px solid var(--border)",transition:"all 0.12s"
            }}>{label}</button>
          ))}
        </div>
        <button style={V.nextBtn} onClick={()=>setStep("text")}>Next: Add News Text →</button>
      </div>
    );

    if(step==="text") return(
      <div>
        <NavRow backStep="photo" backLabel="← Photo"/>
        <SecHdr icon="✍" label="News Headline"/>
        <div style={V.card}>
          <div style={{...V.sub,marginTop:0,fontSize:11,color:"var(--txt-md)"}}>Type or paste your headline</div>
          <div ref={hlBoxRef}
            contentEditable="true"
            suppressContentEditableWarning
            onInput={e=>{
              const html = e.currentTarget.innerHTML;
              lastSetHTML.current = html;
              setHeadHTML(html);
            }}
            onMouseUp={saveSelection}
            onKeyUp={saveSelection}
            onTouchEnd={saveSelection}
            style={{
              background:"var(--bg-deep)",border:"2px solid var(--accent)",borderRadius:6,
              padding:"10px 12px",minHeight:70,fontSize:16,fontWeight:700,
              color:"var(--txt-hi)",fontFamily:"Arial,sans-serif",
              lineHeight:1.8,wordBreak:"break-word",cursor:"text",
              userSelect:"text",WebkitUserSelect:"text",marginBottom:8,
              outline:"none"
            }}/>

          {/* Selection warning */}
          {noSelWarn && (
            <div style={{
              background:"rgba(204,0,0,0.15)",border:"1px solid rgba(204,0,0,0.4)",
              borderRadius:5,padding:"6px 10px",fontSize:11,color:"#ff6b6b",
              marginBottom:6,display:"flex",alignItems:"center",gap:6
            }}>
              ⚠️ <strong>Pehle text select karo</strong> box mein, phir format apply karo
            </div>
          )}
          <div style={{display:"flex",gap:6}}>
            <button onClick={()=>{
              const b = hlBoxRef.current;
              if(b){const html = b.innerHTML;lastSetHTML.current = html;setHeadHTML(html+"_");setTimeout(()=>setHeadHTML(html),10);}
            }} style={{
              flex:1,height:34,borderRadius:5,fontSize:12,fontWeight:700,cursor:"pointer",
              background:"rgba(76,175,80,0.12)",color:"#4caf50",border:"1px solid #4caf50"
            }}>✅ Save to Canvas</button>
            <button onClick={()=>setShowTranslate(!showTranslate)} style={{
              flex:1,height:34,borderRadius:5,fontSize:12,fontWeight:700,cursor:"pointer",
              background:showTranslate?"rgba(66,133,244,0.2)":"rgba(66,133,244,0.08)",
              color:"#4285F4",border:"1px solid #4285F4"
            }}>🌐 Translate</button>
          </div>          {showTranslate&&(
            <div style={{marginTop:8,background:"var(--bg-deep)",borderRadius:5,padding:8,border:"1px solid var(--border)"}}>
              <div style={{fontSize:10,color:"var(--txt-md)",marginBottom:6}}>Type text above → select language → translate:</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:8}}>
                {LANG_OPTIONS.map(({code,label})=>(
                  <button key={code} disabled={translating} onClick={()=>doTranslate(code)} style={{
                    padding:"5px 10px",borderRadius:5,fontSize:11,fontWeight:700,cursor:"pointer",
                    background:"var(--bg-card)",color:"var(--txt-md)",border:"1px solid var(--border)",opacity:translating?0.5:1
                  }}>{translating?"⏳":label}</button>
                ))}
              </div>
              <button onClick={()=>setShowTranslate(false)} style={{fontSize:10,color:"var(--txt-lo)",background:"transparent",border:"none",cursor:"pointer",textDecoration:"underline"}}>Close</button>
            </div>
          )}
        </div>
        <SecHdr icon="🎨" label="Text Formatting"/>
        <div style={V.card}>
          <div style={{
            fontSize:11,color:"#4caf50",marginBottom:8,lineHeight:1.5,
            background:"rgba(76,175,80,0.08)",borderRadius:4,padding:"6px 10px",
            border:"1px solid rgba(76,175,80,0.2)"
          }}>
            💡 <strong>Pehle text select karo</strong> box mein (drag/long press) → phir format apply karo.<br/>
            <span style={{fontSize:10,color:"var(--txt-lo)"}}>Sirf selected text pe effect aayega — poore text pe nahi.</span>
          </div>
          <div style={{...V.sub,marginTop:0}}>Font Family</div>
          <select value={canvasFont} onChange={e=>{
            if(!e.target.value) return;
            setCanvasFont(e.target.value);
            execCmd("fontName", e.target.value);
          }} style={{
            width:"100%",background:"var(--bg-deep)",border:"1px solid var(--border)",
            borderRadius:5,color:"var(--txt-hi)",fontSize:12,padding:"6px 8px",
            outline:"none",marginBottom:8,cursor:"pointer"
          }}>
            <option value="">-- Select Font --</option>
            <optgroup label="Recommended">
              <option value="Arial">Arial</option>
              <option value="Arial Black">Arial Black</option>
              <option value="Arial Narrow">Arial Narrow</option>
              <option value="Calibri">Calibri</option>
              <option value="Georgia">Georgia</option>
              <option value="Impact">Impact</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Trebuchet MS">Trebuchet MS</option>
              <option value="Verdana">Verdana</option>
            </optgroup>
            <optgroup label="Hindi / Devanagari">
              <option value="Noto Sans Devanagari">Noto Sans Devanagari</option>
              <option value="Mangal">Mangal</option>
              <option value="Aparajita">Aparajita</option>
              <option value="Utsaah">Utsaah</option>
              <option value="Kokila">Kokila</option>
              <option value="Raavi">Raavi</option>
            </optgroup>
            <optgroup label="All Fonts">
              <option value="Agency FB">Agency FB</option>
              <option value="Bahnschrift">Bahnschrift</option>
              <option value="Cambria">Cambria</option>
              <option value="Century Gothic">Century Gothic</option>
              <option value="Comic Sans MS">Comic Sans MS</option>
              <option value="Courier New">Courier New</option>
              <option value="Franklin Gothic Medium">Franklin Gothic Medium</option>
              <option value="Garamond">Garamond</option>
              <option value="Gill Sans MT">Gill Sans MT</option>
              <option value="Helvetica">Helvetica</option>
              <option value="Noto Sans">Noto Sans</option>
              <option value="Segoe UI">Segoe UI</option>
              <option value="Tahoma">Tahoma</option>
              <option value="Verdana">Verdana</option>
            </optgroup>
          </select>
          <div style={V.sub}>Font Size (select text first)</div>
          <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:8}}>
            {[{sz:1,label:"XS"},{sz:2,label:"S"},{sz:3,label:"M"},{sz:4,label:"L"},{sz:5,label:"XL"},{sz:6,label:"2X"},{sz:7,label:"3X"}].map(({sz,label})=>(
              <button key={sz}
                onMouseDown={e=>e.preventDefault()}
                onClick={()=>execCmd("fontSize",sz)} style={{
                flex:1,height:30,borderRadius:5,fontSize:10,fontWeight:700,cursor:"pointer",
                background:"var(--bg-deep)",color:"var(--txt-md)",border:"1px solid var(--border)",transition:"all 0.12s"
              }}>{label}</button>
            ))}
          </div>
          <div style={V.sub}>Style</div>
          <div style={{display:"flex",gap:6,marginBottom:8}}>
            <button
              onMouseDown={e=>e.preventDefault()}
              onClick={()=>{execCmd("bold");setCanvasBold(v=>!v);}} style={{
              flex:1,height:36,borderRadius:5,fontSize:14,fontWeight:900,cursor:"pointer",
              background:canvasBold?"rgba(212,165,32,0.2)":"var(--bg-deep)",
              color:canvasBold?"#D4A520":"var(--txt-hi)",
              border:canvasBold?"1.5px solid #D4A520":"1px solid var(--border)"}}>B</button>
            <button
              onMouseDown={e=>e.preventDefault()}
              onClick={()=>{execCmd("italic");setCanvasItalic(v=>!v);}} style={{
              flex:1,height:36,borderRadius:5,fontSize:14,fontWeight:700,fontStyle:"italic",cursor:"pointer",
              background:canvasItalic?"rgba(212,165,32,0.2)":"var(--bg-deep)",
              color:canvasItalic?"#D4A520":"var(--txt-hi)",
              border:canvasItalic?"1.5px solid #D4A520":"1px solid var(--border)"}}>I</button>
            <button
              onMouseDown={e=>e.preventDefault()}
              onClick={()=>execCmd("underline")} style={{
              flex:1,height:36,borderRadius:5,fontSize:13,fontWeight:700,textDecoration:"underline",cursor:"pointer",
              background:"var(--bg-deep)",color:"var(--txt-hi)",border:"1px solid var(--border)"}}>U</button>
            <button
              onMouseDown={e=>e.preventDefault()}
              onClick={resetColors} style={{
              flex:1,height:36,borderRadius:5,fontSize:11,cursor:"pointer",
              background:"rgba(204,0,0,0.1)",color:"#CC0000",border:"1px solid rgba(204,0,0,0.3)"}}>✕ Clear</button>
          </div>
          <div style={V.sub}>Text Color → Canvas</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:6}}>
            {[
              {c:"#CC0000",label:"Red",fg:"#fff"},{c:"#D4A520",label:"Gold",fg:"#000"},
              {c:"#ffffff",label:"White",fg:"#111",bdr:"1px solid #555"},{c:"#000000",label:"Black",fg:"#fff"},
              {c:"#4CAF50",label:"Green",fg:"#fff"},{c:"#2196F3",label:"Blue",fg:"#fff"},
              {c:"#FF6D00",label:"Orange",fg:"#fff"},{c:"#E91E63",label:"Pink",fg:"#fff"},
              {c:"#FFFF00",label:"Yellow",fg:"#111"},{c:"#9C27B0",label:"Purple",fg:"#fff"},
              {c:"#00BCD4",label:"Cyan",fg:"#111"},
            ].map(({c,label,fg,bdr})=>(
              <button key={c}
                onMouseDown={e=>e.preventDefault()}
                onClick={()=>{setSelColor(c);setCanvasColor(c);execCmd("foreColor",c);}} style={{
                padding:"4px 8px",borderRadius:4,fontSize:10,fontWeight:700,cursor:"pointer",
                background:c,color:fg,
                border:canvasColor===c?"2.5px solid #fff":(bdr||"1.5px solid transparent"),
                transform:canvasColor===c?"scale(1.08)":"scale(1)",transition:"all 0.12s"
              }}>{label}</button>
            ))}
            <label title="Custom Color" style={{display:"flex",alignItems:"center",gap:3,cursor:"pointer"}}>
              <input type="color" value={selColor} onChange={e=>{
                const c=e.target.value;setSelColor(c);setCanvasColor(c);execCmd("foreColor",c);
              }} style={{width:28,height:28,borderRadius:4,border:"1px solid var(--border)",cursor:"pointer",padding:0}}/>
              <span style={{fontSize:10,color:"var(--txt-lo)"}}>Custom</span>
            </label>
          </div>
          <div style={{display:"flex",gap:4}}>
            <button onMouseDown={e=>e.preventDefault()} onClick={()=>execCmd("undo")} style={{flex:1,height:28,borderRadius:4,fontSize:11,cursor:"pointer",background:"var(--bg-deep)",color:"var(--txt-md)",border:"1px solid var(--border)"}}>↩ Undo</button>
            <button onMouseDown={e=>e.preventDefault()} onClick={()=>execCmd("redo")} style={{flex:1,height:28,borderRadius:4,fontSize:11,cursor:"pointer",background:"var(--bg-deep)",color:"var(--txt-md)",border:"1px solid var(--border)"}}>↪ Redo</button>
          </div>
        </div>
        <SecHdr icon="⚙️" label="Other Fields"/>
        <div style={V.card}>
          <div style={V.sub}>Source / Channel</div>
          <input className="control-input" value={source} onChange={e=>setSource(e.target.value)} style={{marginBottom:8}}/>
          <div style={V.sub}>Badge Text</div>
          <input className="control-input" value={badgeText} onChange={e=>setBadgeText(e.target.value)} style={{marginBottom:8}}/>
          <div style={V.sub}>LIVE Label</div>
          <input className="control-input" value={liveLabel} onChange={e=>setLiveLabel(e.target.value)}/>
        </div>
        <button style={V.nextBtn} onClick={doneText}>Next: Add Your Logo →</button>
      </div>
    );

    if(step==="logo") return(
      <div>
        <NavRow backStep="text" backLabel="← Text"/>
        <SecHdr icon="📰" label="Your Channel Logo"/>

        {/* Global logo shortcut — if set in App */}
        {globalLogo?.url && !logoPrev && (
          <div style={{
            background:"rgba(212,165,32,0.08)", border:"1.5px solid rgba(212,165,32,0.3)",
            borderRadius:8, padding:"10px 12px", marginBottom:10,
            display:"flex", alignItems:"center", gap:10
          }}>
            <img src={globalLogo.url} alt="" style={{
              width:42, height:42, objectFit:"contain", borderRadius:"50%",
              border:"1px solid #D4A520", background:"#111"
            }}/>
            <div style={{flex:1}}>
              <div style={{fontSize:11,fontWeight:700,color:"#D4A520",marginBottom:3}}>✨ Channel Logo Available</div>
              <div style={{fontSize:10,color:"var(--txt-lo)",marginBottom:5}}>Login ke waqt upload kiya hua logo use karo</div>
              <button onClick={()=>{
                const url = globalLogo.url;
                setLogoCloudUrl(url); setLogoPrev(url);
                const img=new Image(); img.crossOrigin="anonymous";
                img.onload=()=>{ logoRef.current=img; redraw(); };
                img.src=url;
              }} style={{
                height:28, padding:"0 12px", borderRadius:5, fontSize:11, fontWeight:700,
                background:"linear-gradient(135deg,rgba(212,165,32,0.2),rgba(212,165,32,0.1))",
                color:"#D4A520", border:"1px solid #D4A520", cursor:"pointer"
              }}>⚡ Use Channel Logo</button>
            </div>
          </div>
        )}

        <label style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,border:"2px dashed var(--border-hi)",borderRadius:8,padding:"16px 10px",cursor:"pointer",background:"var(--bg-card)",position:"relative",overflow:"hidden",marginBottom:8}}>
          <input type="file" accept="image/*" hidden onChange={handleLogoFile}/>
          <span style={{fontSize:30}}>{logoUploading?"⏳":"📰"}</span>
          <span style={{fontSize:12,fontWeight:700,color:"var(--txt-md)"}}>
            {logoUploading?"Uploading to Cloud...":"UPLOAD DIFFERENT LOGO"}
          </span>
          <span style={{fontSize:10,color:"var(--txt-lo)"}}>
            {logoCloudUrl?"☁️ Saved on Cloudinary":"PNG with transparent background recommended"}
          </span>
        </label>
        {logoPrev&&(
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
            <img src={logoPrev} alt="" style={{width:54,height:54,objectFit:"contain",borderRadius:logoShape==="circle"?"50%":logoShape==="square"?"6px":"4px",border:"1px solid var(--border)",background:"#111"}}/>
            <div style={{flex:1}}>
              <div style={{fontSize:11,color:"var(--green)",marginBottom:4}}>✅ Logo uploaded!</div>
              <button onClick={resetLogo} style={{height:26,borderRadius:4,background:"transparent",border:"1px solid var(--border-hi)",color:"var(--txt-md)",fontSize:10,fontWeight:700,cursor:"pointer",padding:"0 10px"}}>↩ Remove Logo</button>
            </div>
          </div>
        )}
        <SecHdr icon="🔵" label="Logo Shape"/>
        <div style={{display:"flex",gap:6,marginBottom:10}}>
          {[["circle","⬤ Circle"],["square","■ Square"],["diamond","◆ Diamond"]].map(([v,l])=>(
            <button key={v} onClick={()=>setLogoShape(v)} style={{
              flex:1,padding:"7px 4px",borderRadius:5,fontSize:11,fontWeight:700,cursor:"pointer",
              background:logoShape===v?"rgba(204,34,0,0.15)":"var(--bg-card)",
              color:logoShape===v?"#fff":"var(--txt-lo)",
              border:logoShape===v?"1.5px solid var(--red)":"1px solid var(--border)",transition:"all 0.12s"
            }}>{l}</button>
          ))}
        </div>
        <SecHdr icon="🔍" label="Logo Size & Border"/>
        <div style={V.card}>
          <Sl label="Size" value={logoSize} setter={setLogoSize} min={5} max={20}/>
          <Sl label="Border Width" value={logoBorderWidth} setter={setLogoBorderWidth} min={0} max={5} step={0.5}/>
          <div style={V.sub}>Border Color</div>
          <Sw colors={SWATCHES_LOGO_BORDER} sel={logoBorderColor} fn={setLogoBorderColor}/>
        </div>
        <SecHdr icon="🔍" label="Logo Position"/>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:5,marginBottom:12}}>
          {LOGO_POS.map(([k,label])=>(
            <button key={k} onClick={()=>setLogoPosKey(k)} style={{
              padding:"10px 4px",borderRadius:5,fontSize:16,cursor:"pointer",textAlign:"center",
              background:logoPosKey===k?"rgba(212,165,32,0.15)":"var(--bg-card)",
              color:logoPosKey===k?"var(--accent)":"var(--txt-lo)",
              border:logoPosKey===k?"1.5px solid var(--accent)":"1px solid var(--border)",transition:"all 0.12s"
            }}>{label}</button>
          ))}
        </div>
        <button style={{...V.nextBtn,background:"linear-gradient(135deg,#1b5e20,#0d3d10)"}} onClick={downloadPNG}>
          ⬇️ Save & Download PNG
        </button>
        <button style={{...V.nextBtn,background:"transparent",border:"1px solid var(--border-hi)",color:"var(--txt-md)",marginTop:6}} onClick={()=>setStep("template")}>
          ← Back to Template
        </button>
      </div>
    );
  }

  // ── Mobile detect ──
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  // ══════════════════════════════════════════════════════════
  // MOBILE LAYOUT — Canvas top, bottom sheet controls
  // ══════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 46px)",overflow:"hidden",background:"#060608"}}>
        <PreviewModal/>

        {/* ── TOP: Canvas area ── */}
        <div style={{flex:"0 0 auto",display:"flex",flexDirection:"column",background:"#060608"}}>

          {/* Template ribbon — horizontal scroll */}
          <div style={{display:"flex",gap:5,padding:"6px 10px",overflowX:"auto",background:"var(--bg-base)",borderBottom:"1px solid var(--border)",WebkitOverflowScrolling:"touch"}}>
            {TEMPLATES.map(t=>(
              <button key={t.id} onClick={()=>{setTemplate(t.id);setStep("template");}} style={{
                padding:"5px 10px",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",flexShrink:0,
                background:template===t.id?"rgba(204,34,0,0.2)":"var(--bg-card)",
                color:template===t.id?"#fff":"var(--txt-lo)",
                border:template===t.id?"1.5px solid var(--red)":"1px solid var(--border)",
                display:"flex",alignItems:"center",gap:4,whiteSpace:"nowrap"
              }}>{t.emoji} {t.label}</button>
            ))}
          </div>

          {/* Canvas — compact height on mobile */}
          <div style={{display:"flex",justifyContent:"center",alignItems:"center",padding:"8px",background:"#060608"}}>
            <canvas ref={canvasRef} width={400} height={500}
              style={{height:"200px",width:"160px",borderRadius:4,boxShadow:"0 4px 24px rgba(0,0,0,0.9)"}}/>
          </div>

          {/* Download button */}
          <button onClick={downloadPNG} style={{
            margin:"0 10px 8px",height:42,
            background:"linear-gradient(135deg,#CC0000,#880000)",
            color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:800,
            letterSpacing:1,cursor:"pointer"
          }}>⬇️ Download PNG</button>
        </div>

        {/* ── BOTTOM: Step tabs + content ── */}
        <div style={{flex:1,display:"flex",flexDirection:"column",background:"var(--bg-panel)",borderTop:"2px solid var(--border)",overflow:"hidden"}}>

          {/* Step tabs — icon + label */}
          <div style={{display:"flex",background:"var(--bg-base)",borderBottom:"1px solid var(--border)",flexShrink:0}}>
            {STEPS.map((s,i)=>{
              const done=STEPS.findIndex(x=>x.id===step)>i;
              const active=step===s.id;
              return(
                <button key={s.id} onClick={()=>setStep(s.id)} style={{
                  flex:1,display:"flex",flexDirection:"column",alignItems:"center",
                  gap:2,padding:"8px 2px",cursor:"pointer",border:"none",
                  background:active?"rgba(204,34,0,0.12)":"transparent",
                  borderBottom:active?"2px solid var(--red)":"2px solid transparent",
                  transition:"all 0.15s"
                }}>
                  <span style={{fontSize:18}}>{done?"✅":s.icon}</span>
                  <span style={{fontSize:9,fontWeight:700,color:active?"#fff":done?"#4caf50":"var(--txt-lo)",letterSpacing:0.5}}>{s.label}</span>
                </button>
              );
            })}
          </div>

          {/* Step content — scrollable */}
          <div style={{flex:1,overflowY:"auto",padding:"10px 12px",WebkitOverflowScrolling:"touch"}}>
            {renderStepContent()}
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // DESKTOP LAYOUT — unchanged
  // ══════════════════════════════════════════════════════════
  return(
    <div style={{display:"flex",height:"calc(100vh - 46px)",overflow:"hidden",background:"#060608"}}>
      <PreviewModal/>
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{display:"flex",background:"var(--bg-base)",borderBottom:"1px solid var(--border)",padding:"6px 10px",gap:4,flexShrink:0}}>
          {STEPS.map((s,i)=>{
            const done=STEPS.findIndex(x=>x.id===step)>i;
            const active=step===s.id;
            return(
              <button key={s.id} onClick={()=>setStep(s.id)} style={{
                display:"flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:5,fontSize:10,fontWeight:700,cursor:"pointer",
                background:active?"rgba(204,34,0,0.15)":done?"rgba(76,175,80,0.08)":"var(--bg-card)",
                color:active?"#fff":done?"#4caf50":"var(--txt-lo)",
                border:active?"1.5px solid var(--red)":done?"1px solid #4caf50":"1px solid var(--border)",
                transition:"all 0.15s"
              }}>
                <span>{done?"✅":s.icon}</span>
                <span style={{display:"flex",alignItems:"center",gap:3}}>
                  <span style={{color:active?"#ff6666":done?"#4caf50":"var(--txt-lo)",fontSize:9}}>{i+1}.</span>
                  {s.label}
                </span>
              </button>
            );
          })}
          <div style={{flex:1}}/>
          <button onClick={downloadPNG} style={{
            padding:"5px 14px",borderRadius:5,fontSize:10,fontWeight:700,cursor:"pointer",
            background:"linear-gradient(135deg,#CC0000,#880000)",color:"#fff",border:"none"
          }}>⬇️ Download</button>
        </div>
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",padding:"10px 10px 4px",background:"#060608"}}>
          <canvas ref={canvasRef} width={400} height={500}
            style={{maxWidth:"100%",maxHeight:"100%",aspectRatio:"4/5",boxShadow:"0 8px 48px rgba(0,0,0,0.95)",borderRadius:3}}/>
        </div>
        <div style={{background:"var(--bg-base)",borderTop:"1px solid var(--border)",padding:"5px 8px",display:"flex",gap:4,overflowX:"auto",flexShrink:0}}>
          {TEMPLATES.map(t=>(
            <button key={t.id} onClick={()=>{setTemplate(t.id);setStep("template");}} style={{
              padding:"4px 9px",borderRadius:5,fontSize:10,fontWeight:700,cursor:"pointer",
              background:template===t.id?"rgba(204,34,0,0.2)":"var(--bg-card)",
              color:template===t.id?"#fff":"var(--txt-lo)",
              border:template===t.id?"1.5px solid var(--red)":"1px solid var(--border)",
              transition:"all 0.15s",flexShrink:0,whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:3
            }}>{t.emoji} {t.label}</button>
          ))}
        </div>
      </div>
      <div style={{
        width:300,flexShrink:0,background:"var(--bg-panel)",
        borderLeft:"1px solid var(--border)",display:"flex",flexDirection:"column",overflow:"hidden"
      }}>
        <div style={{padding:"10px 12px 8px",background:"var(--bg-base)",borderBottom:"1px solid var(--border)",flexShrink:0}}>
          <div style={{fontSize:9,color:"var(--txt-lo)",letterSpacing:"1.5px",textTransform:"uppercase",marginBottom:2}}>
            {activeTpl.emoji} {activeTpl.label} Template
          </div>
          <div style={{fontSize:13,fontWeight:700,color:"var(--txt-hi)"}}>
            {STEPS.find(s=>s.id===step)?.icon} {STEPS.find(s=>s.id===step)?.label}
          </div>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:12}}>
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
}
